import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useAccount, useBalance, useWalletClient } from "wagmi";
import { LiFiWidget } from "@lifi/widget";
import Nav from "../../components/Nav";
import Footer from "../../components/Footer";
import "./bobdashboard.scss";

const BOB_TOKEN = "0x21E325B059Cd83d4037C82F0F5998Ba2dF3d7777";
const BOB_PAIR = "0xe5a8f88c3db25434b6b4320a8e14348c447dd7a8";
const BOB_TOTAL_SUPPLY = 1_000_000_000;
const BASE_CHAIN_ID = 8453;
const BASE_NATIVE = "0x0000000000000000000000000000000000000000";
const LI_FI_CHAIN_ID = 143;
const ONRAMP_SESSION_ENDPOINT =
  process.env.REACT_APP_ONRAMP_SESSION_ENDPOINT || "/api/coinbase/onramp-session";
const DASHBOARD_API_BASE = (process.env.REACT_APP_DASHBOARD_API_BASE || "").replace(/\/+$/, "");
const X_TASKS_API_BASE = (process.env.REACT_APP_X_TASKS_API_BASE || DASHBOARD_API_BASE || "https://api.bobmonad.com").replace(/\/+$/, "");
const ANALYTICS_STATS_POLL_MS = 10_000;
const ANALYTICS_CURSOR_MS = 1_000;
const ANALYTICS_OHLCV_REFRESH_MS = 60_000;
const ANALYTICS_HOLDERS_POLL_MS = 120_000;
const TASKS_POLL_MS = 60_000;
const X_VERIFY_RESET_MS = 180_000;
const X_LIKE_RT_VERIFY_DELAY_MS = 5_000;
const X_FOLLOW_VERIFY_DELAY_MS = 5_000;
const PENDING_VERIFY_TTL_MS = 10 * 60_000;
const CHART_CACHE_KEY = "bob_dashboard_chart_points_v1";
const COMMUNITY_MEMES = [
  "/bass/bob_chillin.webp",
  "/bass/bob_eyes.png",
  "/bass/bobart.jpg",
  "/bass/bobsale.png",
  "/bass/bobtrade.png",
];
const FEATURED_EVENT_DRAWS = [
  {
    id: "bob-draw-latest",
    image: `${process.env.PUBLIC_URL || ""}/bobdraws/bob_38.png`,
    winner: "Bartman",
  },
];
const EVENT_TIMEZONE = "America/New_York";
const EVENT_DAY = 3;
const EVENT_START_HOUR = 14;
const EVENT_END_HOUR = 16;
const EVENT_DRAW_ROTATE_MS = 10_000;
const EVENT_DRAW_FADE_MS = 900;
const COMMUNITY_CREATOR_ITEMS = [
  {
    id: "gbob-song",
    title: "GBOB",
    creator: "di4blo",
    type: "Music Drop",
    description: "Start your day with the latest EDM Bob banger.",
    image: "/creatorspotlight/bobsong.jpg",
    audio: "/creatorspotlight/bobsong.mp3",
  },
  {
    id: "inferno",
    title: "Inferno",
    creator: "Wampa1",
    type: "Burn Tool",
    description: "Community burn utility for reducing BOB supply.",
    image: "/creatorspotlight/bobburn.png",
    href: "https://inferno.monadfan.xyz/",
  },
  {
    id: "bob-to-the-moon",
    title: "Bob to the Moon",
    creator: "livingliketheboss",
    type: "Arcade Game",
    description: "Arcade style shooter with Bob in the cockpit.",
    image: "/creatorspotlight/bobgame.png",
    href: "https://buildathons.replit.app/projects/078adf8c-f6c1-46a1-a820-009c1e19e38c",
  },
];
const COMMUNITY_TASK_ROWS = [
  { id: "daily_check_in", title: "Daily Check-In (10 pts)", points: 10, buttonLabel: "Check In" },
  { id: "x_connect_twitter", title: "Connect Twitter account (10 pts)", points: 10 },
  { id: "x_follow_bobmonad", title: "Follow Bob on Twitter (20 pts)", points: 20 },
  { id: "x_like_rt_latest", title: "Like and retweet latest post (10 pts)", points: 10 },
  { id: "capricorn_lp_bob_wmon", title: "Add liquidity to Capricorn (20 pts)", points: 20, url: "https://app.capricorn.exchange/pools/0xe5a8f88c3db25434b6b4320a8e14348c447dd7a8", buttonLabel: "Open Capricorn" },
  { id: "join_discord", title: "Join Bob Discord (10 pts)", points: 10 },
  { id: "join_telegram", title: "Join Bob Telegram (10 pts)", points: 10 },
  { id: "visit_opensea", title: "Visit Bob on OpenSea (5 pts)", points: 5 },
];
const TASK_PRIORITY = {
  daily_check_in: 0,
  x_connect_twitter: 0,
  x_follow_bobmonad: 1,
};
const LIKE_RT_TASK_ID = "x_like_rt_latest";
const CAPRICORN_LP_TASK_ID = "capricorn_lp_bob_wmon";
const CAPRICORN_VERIFY_RESET_MS = 10 * 60_000;
const PENDING_VERIFY_STORAGE_KEY = "bob_x_pending_verify_v1";
const DEFAULT_LATEST_TWEET_URL = "https://x.com/BobMonad/status/2052908597537894841?s=20";
const PROTECTED_TASK_IDS = new Set(["x_connect_twitter", "x_follow_bobmonad"]);
const DASHBOARD_CACHE_NAMESPACE = "bob_dashboard_cache_v1";
const TASK_EXTERNAL_URLS = {
  x_follow_bobmonad: "https://x.com/bobmonad",
  join_discord: "https://discord.gg/c69kaPBcav",
  join_telegram: "https://t.me/bobonmonad",
  visit_opensea: "https://opensea.io/token/monad/0x21e325b059cd83d4037c82f0f5998ba2df3d7777?timeframe=seven_days&candleSize=four_hours",
  [CAPRICORN_LP_TASK_ID]: "https://app.capricorn.exchange/pools/0xe5a8f88c3db25434b6b4320a8e14348c447dd7a8",
};
const BURN_WALLETS = new Set([
  "0x0000000000000000000000000000000000000000",
  "0x000000000000000000000000000000000000dead",
  "0xdead000000000000000042069420694206942069",
]);
const LP_HOLDER_LABELS = {
  "0xe5a8f88c3db25434b6b4320a8e14348c447dd7a8": "Capricorn LP",
  "0x188d586ddcf52439676ca21a244753fa19f9ea8e": "Uniswap LP",
};
const HOLDER_EXPLORER_BASE = "https://monadvision.com/address";
const DEV_TASKS_WALLET = "0x334270d541d4f9a8717d30690c72159724094323";
const PAGE_TAB_SET = new Set(["analytics", "swap", "tasks", "community", "inventory"]);
const BUY_TAB_SET = new Set(["fiat", "exchange", "bridge"]);

const shorten = (addr = "") => (addr ? `${addr.slice(0, 6)}...${addr.slice(-4)}` : "-");
const shortenCompact = (addr = "") => (addr ? `${addr.slice(0, 4)}...${addr.slice(-3)}` : "-");
const shortenTiny = (addr = "") => (addr ? `${addr.slice(0, 4)}..${addr.slice(-2)}` : "-");
const fmtNum = (n, max = 2) =>
  Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: max }) : "-";
const fmtUsd = (n) => {
  if (!Number.isFinite(n)) return "-";
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  if (n < 0.01) return `$${n.toFixed(6)}`;
  return `$${n.toFixed(4)}`;
};
const fmtUsdWhole = (n) => {
  if (!Number.isFinite(n)) return "-";
  if (n >= 1_000_000_000) return `$${Math.round(n / 1_000_000_000)}B`;
  if (n >= 1_000_000) return `$${Math.round(n / 1_000_000)}M`;
  if (n >= 1_000) return `$${Math.round(n / 1_000)}K`;
  return `$${Math.round(n)}`;
};
const fmtBobCompact = (n) => {
  if (!Number.isFinite(n)) return "-";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2).replace(/\.00$/, "")}b`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return `${Math.round(n)}`;
};
const fmtBobHolding = (n) => {
  if (!Number.isFinite(n)) return "-";
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(2).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1")}b`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n < 10_000_000 ? 2 : 1).replace(/\.00$/, "").replace(/(\.\d)0$/, "$1")}m`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, "")}k`;
  return fmtNum(n, 2);
};
const fmtUsdCents = (n) => (Number.isFinite(n) ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "-");

function readDashboardCache(key, fallback = null) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(`${DASHBOARD_CACHE_NAMESPACE}:${key}`);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed?.value ?? fallback;
  } catch {
    return fallback;
  }
}

function writeDashboardCache(key, value) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      `${DASHBOARD_CACHE_NAMESPACE}:${key}`,
      JSON.stringify({
        savedAt: Date.now(),
        value
      })
    );
  } catch {}
}
const fmtUsdCompact = (n) => {
  if (!Number.isFinite(n)) return "-";
  if (n < 1_000) {
    if (n >= 100) return `$${Math.round(n).toLocaleString()}`;
    return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  if (n >= 100_000_000) return `$${(n / 1_000_000).toFixed(0)}m`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1).replace(/\.0$/, "")}m`;
  if (n >= 100_000) return `$${Math.round(n / 1_000)}k`;
  return `$${(n / 1_000).toFixed(2).replace(/(\.\d)0$/, "$1")}k`;
};
const getHolderBadge = (rank) => {
  if (rank === 1) return "King Bob";
  if (rank <= 3) return "Top Bob";
  if (rank <= 10) return "Giga Bob";
  if (rank <= 25) return "Mega Bob";
  if (rank <= 50) return "Super Bob";
  return "Big Bob";
};
const getHolderBadgeClass = (rank) => {
  if (rank === 1) return "is-supreme";
  if (rank <= 3) return "is-elite";
  if (rank <= 10) return "is-core";
  if (rank <= 25) return "is-strong";
  return "is-base";
};
const getHolderBadgeMeta = (wallet = "", nonLpRank = 0) => {
  const key = String(wallet || "").toLowerCase();
  const lpLabel = LP_HOLDER_LABELS[key];
  if (lpLabel) return { label: lpLabel, className: "is-lp" };
  return { label: getHolderBadge(nonLpRank), className: getHolderBadgeClass(nonLpRank) };
};
const getEventCountdownState = (clock = new Date()) => {
  const nowEt = new Date(clock.toLocaleString("en-US", { timeZone: EVENT_TIMEZONE }));
  const day = nowEt.getDay();
  const start = new Date(nowEt);
  start.setHours(EVENT_START_HOUR, 0, 0, 0);
  const end = new Date(nowEt);
  end.setHours(EVENT_END_HOUR, 0, 0, 0);
  const isLive = day === EVENT_DAY && nowEt >= start && nowEt < end;

  let nextStart = new Date(nowEt);
  if (isLive) {
    nextStart = end;
  } else {
    let daysUntil = (EVENT_DAY - day + 7) % 7;
    if (daysUntil === 0 && nowEt >= end) daysUntil = 7;
    nextStart.setDate(nowEt.getDate() + daysUntil);
    nextStart.setHours(EVENT_START_HOUR, 0, 0, 0);
  }

  const ms = Math.max(0, nextStart.getTime() - nowEt.getTime());
  const total = Math.floor(ms / 1000);
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const mins = Math.floor((total % 3600) / 60);
  const secs = total % 60;
  let countdown = "0s";
  if (days > 0) countdown = `${days}d`;
  else if (hours > 0) countdown = `${hours}h`;
  else if (mins > 0) countdown = `${mins}min`;
  else countdown = `${secs}s`;

  return { isLive, countdown };
};
function PriceLine({
  points,
  compact = false,
  yDomain = null,
  domainEndMs = null,
  supplyMultiplier = BOB_TOTAL_SUPPLY,
  highLabel = "24H High",
  lowLabel = "24H Low",
}) {
  const width = compact ? 420 : 760;
  const height = compact ? 270 : 290;
  const plotLeft = 0;
  const plotTop = 14;
  const plotRight = 0;
  const plotBottom = 34;
  const plotWidth = width - plotLeft - plotRight;
  const plotHeight = height - plotTop - plotBottom;
  const sortedPoints = points
    .filter((p) => Number.isFinite(p?.t) && Number.isFinite(p?.v))
    .sort((a, b) => a.t - b.t);
  const effectiveEnd = Number.isFinite(domainEndMs)
    ? domainEndMs
    : sortedPoints.length
      ? sortedPoints[sortedPoints.length - 1].t
      : Date.now();
  const effectiveStart = effectiveEnd - 24 * 60 * 60 * 1000;
  const validPoints = sortedPoints.filter((p) => p.t >= effectiveStart && p.t <= effectiveEnd);
  // Keep the trace anchored to the right edge even when the latest fetched sample
  // is slightly behind the moving domain end.
  const renderPoints = (() => {
    if (!validPoints.length) return [];
    let anchored = [...validPoints];
    const first = anchored[0];
    const last = anchored[anchored.length - 1];
    if (first && Number.isFinite(first.t) && Number.isFinite(first.v) && first.t > effectiveStart) {
      anchored = [{ t: effectiveStart, v: first.v }, ...anchored];
    }
    if (last && Number.isFinite(last.t) && Number.isFinite(last.v) && last.t < effectiveEnd) {
      anchored = [...anchored, { t: effectiveEnd, v: last.v }];
    }
    return anchored;
  })();
  const vals = renderPoints.map((p) => p.v);
  const rawMin = vals.length ? Math.min(...vals) : 0;
  const rawMax = vals.length ? Math.max(...vals) : 1;
  const center = (rawMin + rawMax) / 2 || 0;
  const rawRange = Math.max(1e-12, rawMax - rawMin);
  const minVisualRange = Math.max(Math.abs(center) * 0.04, rawRange);
  const expandedMin = center - minVisualRange / 2;
  const expandedMax = center + minVisualRange / 2;
  const min = yDomain && Number.isFinite(yDomain.min) ? Math.min(yDomain.min, expandedMin) : expandedMin;
  const max = yDomain && Number.isFinite(yDomain.max) ? Math.max(yDomain.max, expandedMax) : expandedMax;
  const range = max - min || 1;
  const minT = effectiveStart;
  const maxT = effectiveEnd;
  const timeRange = maxT - minT || 1;
  const minMcap = rawMin * Math.max(0, Number(supplyMultiplier || 0));
  const maxMcap = rawMax * Math.max(0, Number(supplyMultiplier || 0));
  const smoothVals = (() => {
    if (vals.length < 3) return vals;
    return vals.map((v, i) => {
      const a = vals[i - 1] ?? v;
      const b = v;
      const c = vals[i + 1] ?? v;
      return (a + 2 * b + c) / 4;
    });
  })();

  const coordsAll = smoothVals.map((v, i) => {
    const t = Math.max(minT, Math.min(maxT, renderPoints[i]?.t ?? minT));
    const x = plotLeft + ((t - minT) / timeRange) * plotWidth;
    const rawY = plotTop + (1 - (v - min) / range) * plotHeight;
    const y = Math.max(plotTop, Math.min(plotTop + plotHeight, rawY));
    return { x, y };
  });
  const coords = coordsAll;

  const toMidpointCurvePath = (pts) => {
    if (!pts.length) return "";
    if (pts.length === 1) return `M${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)}`;
    if (pts.length === 2) {
      return `M${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)} L${pts[1].x.toFixed(2)},${pts[1].y.toFixed(2)}`;
    }
    let d = `M${pts[0].x.toFixed(2)},${pts[0].y.toFixed(2)}`;
    for (let i = 1; i < pts.length - 1; i += 1) {
      const p1 = pts[i];
      const p2 = pts[i + 1];
      const mx = (p1.x + p2.x) / 2;
      const my = (p1.y + p2.y) / 2;
      d += ` Q${p1.x.toFixed(2)},${p1.y.toFixed(2)} ${mx.toFixed(2)},${my.toFixed(2)}`;
    }
    const last = pts[pts.length - 1];
    d += ` T${last.x.toFixed(2)},${last.y.toFixed(2)}`;
    return d;
  };
  const smoothPath = toMidpointCurvePath(coords);
  const areaPath =
    coords.length > 1
      ? `${smoothPath} L${coords[coords.length - 1].x.toFixed(2)},${(plotTop + plotHeight).toFixed(2)} L${coords[0].x.toFixed(2)},${(plotTop + plotHeight).toFixed(2)} Z`
      : "";

  const yGridLines = Array.from({ length: 5 }, (_, i) => {
    const y = plotTop + (plotHeight * i) / 4;
    return y;
  });

  const HOUR = 60 * 60 * 1000;
  const TICK_STEP = 4 * HOUR;
  const firstTick = Math.ceil(minT / TICK_STEP) * TICK_STEP;
  const xTicks = [];
  for (let t = firstTick; t <= maxT; t += TICK_STEP) {
    const x = plotLeft + ((t - minT) / timeRange) * plotWidth;
    xTicks.push({ t, x });
  }

  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="bd-chart-svg" role="img" aria-label="BOB live price trace">
      <defs>
        <linearGradient id="bd-chart-line-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(242, 131, 60, 0.32)" />
          <stop offset="100%" stopColor="rgba(242, 131, 60, 0)" />
        </linearGradient>
      </defs>
      <rect
        x="0.75"
        y="0.75"
        width={width - 1.5}
        height={height - 1.5}
        rx="14"
        ry="14"
        fill="rgba(2, 6, 23, 0.62)"
      />
      {yGridLines.map((y, i) => (
        <line
          key={`y-${i}`}
          x1={plotLeft}
          x2={plotLeft + plotWidth}
          y1={y}
          y2={y}
          stroke="rgba(148, 163, 184, 0.18)"
          strokeWidth="1"
        />
      ))}
      {xTicks.map((tick) => (
        <line
          key={`x-${tick.t}`}
          x1={tick.x}
          x2={tick.x}
          y1={plotTop}
          y2={plotTop + plotHeight}
          stroke="rgba(148, 163, 184, 0.12)"
          strokeWidth="1"
        />
      ))}
      {coords.length > 1 ? (
        <path
          d={areaPath}
          fill="url(#bd-chart-line-fill)"
          stroke="none"
        />
      ) : null}
      {coords.length > 1 ? (
        <path
          d={smoothPath}
          fill="none"
          stroke="#f2833c"
          strokeWidth="2.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          vectorEffect="non-scaling-stroke"
          shapeRendering="geometricPrecision"
        />
      ) : null}
      {xTicks.map((tick) => (
        <text
          key={`tx-${tick.t}`}
          x={tick.x}
          y={height - (compact ? 16 : 12)}
          textAnchor="middle"
          className="bd-chart-min"
        >
          {new Date(tick.t).toLocaleTimeString([], { hour: "numeric", hour12: true }).replace(/\s/g, "")}
        </text>
      ))}
      {vals.length ? (
        <>
            <text x={width - 12} y={plotTop + 10} textAnchor="end" className="bd-chart-min">
              <tspan>{highLabel}: </tspan>
              <tspan className="bd-chart-high">{fmtUsdCompact(maxMcap)}</tspan>
            </text>
            <text x={width - 12} y={plotTop + plotHeight - 8} textAnchor="end" className="bd-chart-min">
              <tspan>{lowLabel}: </tspan>
              <tspan className="bd-chart-low">{fmtUsdCompact(minMcap)}</tspan>
            </text>
        </>
      ) : null}
    </svg>
  );
}

const AnalyticsPriceChart = memo(function AnalyticsPriceChart({
  compact = false,
  apiBase = "",
  supplyMultiplier = BOB_TOTAL_SUPPLY,
  highLabel = "24H High",
  lowLabel = "24H Low",
  ctaLabel = "Open chart",
  showCta = true,
}) {
  const [pricePoints, setPricePoints] = useState([]);
  const [yDomain, setYDomain] = useState(null);
  const [domainEndMs, setDomainEndMs] = useState(() => Math.floor(Date.now() / 60_000) * 60_000);

  useEffect(() => {
    let dead = false;
    try {
      const cached = window.localStorage.getItem(CHART_CACHE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        if (Array.isArray(parsed)) {
          const cleaned = parsed
            .filter((p) => Number.isFinite(p?.t) && Number.isFinite(p?.v))
            .sort((a, b) => a.t - b.t)
            .slice(-96);
          if (cleaned.length) setPricePoints(cleaned);
        }
      }
    } catch {}
    return () => {
      dead = true;
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => {
      setDomainEndMs(Math.floor(Date.now() / 60_000) * 60_000);
    }, ANALYTICS_CURSOR_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!pricePoints.length) return;
    const vals = pricePoints.map((p) => p.v).filter(Number.isFinite);
    if (!vals.length) return;
    const rawMin = Math.min(...vals);
    const rawMax = Math.max(...vals);
    const span = Math.max(rawMax - rawMin, rawMax * 0.002, 1e-12);
    const targetMin = rawMin - span * 0.06;
    const targetMax = rawMax + span * 0.06;

    setYDomain((prev) => {
      if (!prev || !Number.isFinite(prev.min) || !Number.isFinite(prev.max)) {
        return { min: targetMin, max: targetMax };
      }
      // Stable y-domain: only expand when new extremes appear.
      // This avoids per-tick rescaling/jitter while keeping the line in-bounds.
      const nextMin = Math.min(prev.min, targetMin);
      const nextMax = Math.max(prev.max, targetMax);
      if (nextMin === prev.min && nextMax === prev.max) return prev;
      return { min: nextMin, max: nextMax };
    });
  }, [pricePoints]);

  useEffect(() => {
    let dead = false;
    const loadPairForChart = async () => {
      try {
        const res = await fetch(`${apiBase}/api/dashboard/chart-24h`);
        const json = await res.json().catch(() => null);
        if (dead || !json?.ok || !Array.isArray(json?.data)) return;
        const finalPoints = json.data
          .filter((p) => Number.isFinite(p?.t) && Number.isFinite(p?.v))
          .sort((a, b) => a.t - b.t)
          .slice(-96);
        if (!finalPoints.length) return;
        setPricePoints(finalPoints);
        try {
          window.localStorage.setItem(CHART_CACHE_KEY, JSON.stringify(finalPoints));
        } catch {}
      } catch {}
    };
    loadPairForChart();
    const id = setInterval(loadPairForChart, ANALYTICS_OHLCV_REFRESH_MS);
    return () => {
      dead = true;
      clearInterval(id);
    };
  }, [apiBase]);

  return (
    <div className="bd-chart-wrap">
      <PriceLine
        points={pricePoints}
        compact={compact}
        yDomain={yDomain}
        domainEndMs={domainEndMs}
        supplyMultiplier={supplyMultiplier}
        highLabel={highLabel}
        lowLabel={lowLabel}
      />
      {showCta ? (
        <a className="bd-link-btn bd-btn bd-btn-secondary" href={`https://dexscreener.com/monad/${BOB_PAIR}`} target="_blank" rel="noopener noreferrer">{ctaLabel}</a>
      ) : null}
    </div>
  );
});

export default function BobDashboardPage() {
  const getInitialTabs = () => {
    if (typeof window === "undefined") return { page: "analytics", buy: "exchange" };
    const url = new URL(window.location.href);
    const tab = String(url.searchParams.get("tab") || "").toLowerCase();
    const buy = String(url.searchParams.get("buy") || "").toLowerCase();
    const hash = String(url.hash || "").replace(/^#/, "").toLowerCase();
    const hashParts = hash.split(/[/:_-]/).filter(Boolean);
    const hashTab = hashParts[0] || "";
    const hashBuy = hashParts[1] || "";
    const page = PAGE_TAB_SET.has(tab) ? tab : (PAGE_TAB_SET.has(hashTab) ? hashTab : "analytics");
    const buyTab = BUY_TAB_SET.has(buy) ? buy : (BUY_TAB_SET.has(hashBuy) ? hashBuy : "exchange");
    return { page, buy: buyTab };
  };
  const initialTabs = getInitialTabs();
  const [buyTab, setBuyTab] = useState(initialTabs.buy);
  const [pageTab, setPageTab] = useState(initialTabs.page);
  const { address } = useAccount();
  const { data: bobBalanceData, isLoading: bobBalanceLoading } = useBalance({
    address,
    token: BOB_TOKEN,
    chainId: LI_FI_CHAIN_ID,
    query: {
      enabled: Boolean(address),
      refetchInterval: 20_000,
      refetchOnWindowFocus: true,
    },
  });
  const { data: walletClient } = useWalletClient();
  const [pair, setPair] = useState(() => readDashboardCache("stats", null));
  const [holders, setHolders] = useState(() => readDashboardCache("holders_rows", []));
  const [holdersUpdatedAt, setHoldersUpdatedAt] = useState(() => readDashboardCache("holders_updated_at", null));
  const [holderCount, setHolderCount] = useState(() => readDashboardCache("holders_count", 0));
  const [burned, setBurned] = useState(() => readDashboardCache("holders_burned", 0));
  const [holdersError, setHoldersError] = useState("");
  const [holderPage, setHolderPage] = useState(1);
  const [onrampBusy, setOnrampBusy] = useState(false);
  const [signBusy, setSignBusy] = useState(false);
  const [walletSigned, setWalletSigned] = useState(false);
  const [onrampErr, setOnrampErr] = useState("");
  const [compactChart, setCompactChart] = useState(false);
  const [ultraCompactWallets, setUltraCompactWallets] = useState(false);
  const [taskCompletions, setTaskCompletions] = useState({});
  const [pointsLeaderboard, setPointsLeaderboard] = useState(() => readDashboardCache("points_leaderboard", []));
  const [showDevConsole, setShowDevConsole] = useState(false);
  const [devTaskPreviewMode, setDevTaskPreviewMode] = useState("live");
  const [taskCatalog, setTaskCatalog] = useState(() => readDashboardCache("task_catalog", COMMUNITY_TASK_ROWS));
  const [newTaskTitle, setNewTaskTitle] = useState("");
  const [newTaskPoints, setNewTaskPoints] = useState("10");
  const [newTaskUrl, setNewTaskUrl] = useState("");
  const [newTaskButtonLabel, setNewTaskButtonLabel] = useState("");
  const [xConnectPendingVerify, setXConnectPendingVerify] = useState(false);
  const [xFollowPendingVerify, setXFollowPendingVerify] = useState(false);
  const [xLikeRtPendingVerify, setXLikeRtPendingVerify] = useState(false);
  const [xLikeRtBusy, setXLikeRtBusy] = useState(false);
  const [capricornVerifyBusy, setCapricornVerifyBusy] = useState(false);
  const [capricornPendingVerify, setCapricornPendingVerify] = useState(false);
  const [xFollowBusy, setXFollowBusy] = useState(false);
  const [taskActionLoading, setTaskActionLoading] = useState({});
  const [latestTweetId, setLatestTweetId] = useState(() => readDashboardCache("latest_tweet_id", ""));
  const [latestTweetUrl, setLatestTweetUrl] = useState(() => readDashboardCache("latest_tweet_url", DEFAULT_LATEST_TWEET_URL));
  const [devLatestTweetInput, setDevLatestTweetInput] = useState(() => readDashboardCache("latest_tweet_url", DEFAULT_LATEST_TWEET_URL));
  const [devLatestTweetError, setDevLatestTweetError] = useState("");
  const xConnectResetTimerRef = useRef(null);
  const xFollowResetTimerRef = useRef(null);
  const xLikeRtResetTimerRef = useRef(null);
  const capricornResetTimerRef = useRef(null);
  const [xTaskStatus, setXTaskStatus] = useState({
    loading: false,
    connected: false,
    completed: false,
    error: "",
  });


  const queueXConnectVerifyReset = () => {
    if (xConnectResetTimerRef.current) clearTimeout(xConnectResetTimerRef.current);
    xConnectResetTimerRef.current = window.setTimeout(() => {
      setXConnectPendingVerify(false);
      savePendingVerifyState({ connect: false, follow: xFollowPendingVerify, likeRt: xLikeRtPendingVerify });
    }, X_VERIFY_RESET_MS);
  };

  const queueXFollowVerifyReset = () => {
    if (xFollowResetTimerRef.current) clearTimeout(xFollowResetTimerRef.current);
    xFollowResetTimerRef.current = window.setTimeout(() => {
      setXFollowPendingVerify(false);
      savePendingVerifyState({ connect: xConnectPendingVerify, follow: false, likeRt: xLikeRtPendingVerify });
    }, X_VERIFY_RESET_MS);
  };

  const queueXLikeRtVerifyReset = () => {
    if (xLikeRtResetTimerRef.current) clearTimeout(xLikeRtResetTimerRef.current);
    xLikeRtResetTimerRef.current = window.setTimeout(() => {
      setXLikeRtPendingVerify(false);
      savePendingVerifyState({ connect: xConnectPendingVerify, follow: xFollowPendingVerify, likeRt: false });
    }, X_VERIFY_RESET_MS);
  };

  const queueCapricornVerifyReset = () => {
    if (capricornResetTimerRef.current) clearTimeout(capricornResetTimerRef.current);
    capricornResetTimerRef.current = window.setTimeout(() => {
      setCapricornPendingVerify(false);
    }, CAPRICORN_VERIFY_RESET_MS);
  };

  const savePendingVerifyState = (next) => {
    try {
      const normalized = {
        connect: Boolean(next?.connect),
        follow: Boolean(next?.follow),
        likeRt: Boolean(next?.likeRt),
        savedAt: Date.now(),
      };
      window.sessionStorage.setItem(PENDING_VERIFY_STORAGE_KEY, JSON.stringify(normalized));
    } catch {}
  };

  const loadPendingVerifyState = () => {
    try {
      const raw = window.sessionStorage.getItem(PENDING_VERIFY_STORAGE_KEY);
      if (!raw) return { connect: false, follow: false, likeRt: false };
      const parsed = JSON.parse(raw);
      const savedAt = Number(parsed?.savedAt || 0);
      const isFresh = Number.isFinite(savedAt) && savedAt > 0 && (Date.now() - savedAt) <= PENDING_VERIFY_TTL_MS;
      if (!isFresh) {
        return { connect: false, follow: false, likeRt: false };
      }
      return {
        connect: Boolean(parsed?.connect),
        follow: Boolean(parsed?.follow),
        likeRt: Boolean(parsed?.likeRt),
      };
    } catch {
      return { connect: false, follow: false, likeRt: false };
    }
  };

  const loadLatestTweet = async () => {
    try {
      const res = await fetch(`${X_TASKS_API_BASE}/api/tasks/x/latest-tweet`);
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) throw new Error("Latest tweet unavailable");
      const id = String(json.tweetId || "").trim();
      const url = String(json.tweetUrl || "").trim();
      if (id) setLatestTweetId(id);
      if (url) setLatestTweetUrl(url);
      if (url && !devLatestTweetInput) setDevLatestTweetInput(url);
      writeDashboardCache("latest_tweet_id", id);
      writeDashboardCache("latest_tweet_url", url || DEFAULT_LATEST_TWEET_URL);
      return { id, url };
    } catch {
      const id = readDashboardCache("latest_tweet_id", "");
      const url = readDashboardCache("latest_tweet_url", DEFAULT_LATEST_TWEET_URL);
      if (id) setLatestTweetId(id);
      if (url) {
        setLatestTweetUrl(url);
        if (!devLatestTweetInput) setDevLatestTweetInput(url);
      }
      return url ? { id, url } : null;
    }
  };

  const verifyXLikeRtLatest = async () => {
    if (!address) return false;
    setXLikeRtBusy(true);
    try {
      await loadLatestTweet();
      await new Promise((resolve) => window.setTimeout(resolve, X_LIKE_RT_VERIFY_DELAY_MS));
      const res = await fetch(`${X_TASKS_API_BASE}/api/tasks/x/like-rt/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: address, forceFresh: true }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) throw new Error(json?.error || "Like/retweet verification failed");
      if (json.tweetId) setLatestTweetId(String(json.tweetId));
      if (json.tweetUrl) setLatestTweetUrl(String(json.tweetUrl));
      await loadTaskCompletions(address);
      await loadPointsLeaderboard();
      if (json.completed) {
        setXLikeRtPendingVerify(false);
        setXTaskStatus((prev) => ({ ...prev, error: "" }));
      } else {
        setXTaskStatus((prev) => ({ ...prev, error: "" }));
      }
      return Boolean(json.completed);
    } catch (err) {
      await loadTaskCompletions(address);
      await loadPointsLeaderboard();
      setXTaskStatus((prev) => ({ ...prev, error: "" }));
      return false;
    } finally {
      setXLikeRtBusy(false);
    }
  };

  const verifyCapricornLiquidity = async () => {
    if (!address) return false;
    setCapricornVerifyBusy(true);
    try {
      const res = await fetch(`${X_TASKS_API_BASE}/api/tasks/capricorn/complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: address }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) throw new Error(json?.error || "LP verification failed");
      setCapricornPendingVerify(false);
      setXTaskStatus((prev) => ({ ...prev, error: "" }));
      await loadTaskCompletions(address);
      await loadPointsLeaderboard();
      return true;
    } catch (err) {
      setXTaskStatus((prev) => ({ ...prev, error: "" }));
      await loadTaskCompletions(address);
      return false;
    } finally {
      setCapricornVerifyBusy(false);
    }
  };

  const setLatestTweetFromDev = async () => {
    const value = String(devLatestTweetInput || "").trim();
    if (!value) {
      setDevLatestTweetError("Tweet URL is required.");
      return;
    }
    const validPattern = /^https?:\/\/(?:www\.)?(?:x\.com|twitter\.com)\/bobmonad\/status\/\d+(?:\?.*)?$/i;
    if (!validPattern.test(value)) {
      setDevLatestTweetError("Use a valid @bobmonad post URL (x.com/bobmonad/status/{id}).");
      return;
    }
    setDevLatestTweetError("");
    try {
      const res = await fetch(`${X_TASKS_API_BASE}/api/tasks/x/latest-tweet/set`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tweetUrl: value }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) {
        setDevLatestTweetError(String(json?.error || "Failed to set latest tweet."));
        return;
      }
      setLatestTweetId(String(json.tweetId || ""));
      setLatestTweetUrl(String(json.tweetUrl || value));
      setXLikeRtPendingVerify(false);
      setDevLatestTweetError("");
      if (address) {
        await loadTaskCompletions(address);
        await loadPointsLeaderboard();
      }
    } catch {
      setDevLatestTweetError("Failed to set latest tweet.");
    }
  };

  const safeQuoteAmount = "0";
  const perPage = 20;
  const top100 = holders.slice(0, 100);
  const pages = Math.max(1, Math.ceil(top100.length / perPage));
  const pageRows = top100.slice((holderPage - 1) * perPage, holderPage * perPage);
  const nonLpRankMap = useMemo(() => {
    const map = new Map();
    let rank = 0;
    for (const holder of top100) {
      const wallet = String(holder?.wallet || "").toLowerCase();
      if (LP_HOLDER_LABELS[wallet]) continue;
      rank += 1;
      map.set(wallet, rank);
    }
    return map;
  }, [top100]);
  const pairData = pair?.pair || pair || null;
  const previewLeaderboardRows = Array.from({ length: 5 }, (_, i) => pointsLeaderboard[i] || null);
  const leaderboardRows = Array.from({ length: 10 }, (_, i) => pointsLeaderboard[i] || null);
  const personalRow = useMemo(() => {
    const walletKey = String(address || "").toLowerCase();
    const found = pointsLeaderboard.find((r) => String(r.wallet || "").toLowerCase() === walletKey);
    if (found) {
      return {
        rank: found.rank,
        name: found.username ? `@${String(found.username).replace(/^@/, "")}` : shorten(found.wallet),
        points: `${fmtNum(found.points, 0)} pts`,
      };
    }
    return {
      rank: "-",
      name: address ? shorten(address) : "@you",
      points: "0 pts",
    };
  }, [address, pointsLeaderboard]);
  const inventoryWalletKey = String(address || "").toLowerCase();
  const inventoryHolderRow = useMemo(
    () => top100.find((r) => String(r.wallet || "").toLowerCase() === inventoryWalletKey) || null,
    [top100, inventoryWalletKey]
  );
  const inventoryNonLpRank = Number(nonLpRankMap.get(inventoryWalletKey) || 0);
  const inventoryBadgeMeta = useMemo(() => {
    if (!address) return { label: "Connect Wallet", className: "is-base" };
    if (!inventoryHolderRow) return { label: "Lil Bob", className: "is-base" };
    if (LP_HOLDER_LABELS[inventoryWalletKey]) return { label: LP_HOLDER_LABELS[inventoryWalletKey], className: "is-lp" };
    if (inventoryNonLpRank > 0) return getHolderBadgeMeta(inventoryWalletKey, inventoryNonLpRank);
    return { label: "Lil Bob", className: "is-base" };
  }, [address, inventoryHolderRow, inventoryWalletKey, inventoryNonLpRank]);
  const inventoryBobBalance = Number(bobBalanceData?.formatted || 0);
  const inventorySupplyPct = inventoryBobBalance > 0 ? (inventoryBobBalance / BOB_TOTAL_SUPPLY) * 100 : 0;
  const inventoryBalanceLabel = address
    ? (bobBalanceLoading ? "Loading..." : fmtBobHolding(inventoryBobBalance))
    : "-";
  const inventorySupplyLabel = address
    ? (bobBalanceLoading ? "Loading..." : (inventorySupplyPct > 0 && inventorySupplyPct < 0.1 ? "<0.1%" : `${inventorySupplyPct.toFixed(1)}%`))
    : "Percentage";
  const inventoryUsdValue = address
    ? (bobBalanceLoading ? "Loading..." : fmtUsdCompact(inventoryBobBalance * Number(pairData?.priceUsd || 0)))
    : "-";
  const inventoryWalletLabel = address ? shortenTiny(address) : "Not connected";

  const refreshXFollowStatus = async (walletAddress = address, { silent = false } = {}) => {
    if (!walletAddress) {
      setXTaskStatus((prev) => ({ ...prev, connected: false, completed: false, error: "" }));
      return { connected: false, completed: false };
    }
    if (!silent) {
      setXTaskStatus((prev) => ({ ...prev, loading: true, error: "" }));
    }
    try {
      const res = await fetch(
        `${X_TASKS_API_BASE}/api/tasks/x/follow/status?wallet=${encodeURIComponent(walletAddress)}`
      );
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) throw new Error(json?.error || "X task status unavailable");
      const next = {
        loading: false,
        connected: Boolean(json.connected),
        completed: Boolean(json.completed),
        error: "",
      };
      setXTaskStatus((prev) =>
        prev.loading === next.loading &&
        prev.connected === next.connected &&
        prev.completed === next.completed &&
        prev.error === next.error
          ? prev
          : next
      );
      return { connected: next.connected, completed: next.completed };
    } catch (err) {
      if (!silent) {
        const next = {
          loading: false,
          connected: false,
          completed: false,
          error: "",
        };
        setXTaskStatus((prev) =>
          prev.loading === next.loading &&
          prev.connected === next.connected &&
          prev.completed === next.completed &&
          prev.error === next.error
            ? prev
            : next
        );
      }
      return { connected: false, completed: false };
    }
  };

  const openXConnect = async () => {
    if (!address) {
      setXTaskStatus((prev) => ({ ...prev, error: "" }));
      return;
    }
    const isLikelyMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");
    const popup = isLikelyMobile ? null : window.open("", "bob_x_connect", "popup=yes,width=560,height=760");
    if (!isLikelyMobile && !popup) {
      setXTaskStatus((prev) => ({ ...prev, error: "" }));
      return;
    }
    setXTaskStatus((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      const res = await fetch(
        `${X_TASKS_API_BASE}/api/tasks/x/connect-url?wallet=${encodeURIComponent(address)}`
      );
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok || !json?.connectUrl) throw new Error(json?.error || "Failed to start X connect");
      if (isLikelyMobile) {
        window.location.href = json.connectUrl;
      } else {
        popup.location.href = json.connectUrl;
      }
    } catch (err) {
      try {
        popup?.close();
      } catch {}
      setXTaskStatus((prev) => ({ ...prev, loading: false, error: "" }));
    }
  };

  const verifyXFollow = async ({ silent = false } = {}) => {
    if (!address) {
      if (!silent) setXTaskStatus((prev) => ({ ...prev, error: "" }));
      return false;
    }
    if (xFollowBusy) return false;
    setXFollowBusy(true);
    setXTaskStatus((prev) => ({ ...prev, loading: true, error: "" }));
    try {
      await new Promise((resolve) => window.setTimeout(resolve, X_FOLLOW_VERIFY_DELAY_MS));
      const res = await fetch(`${X_TASKS_API_BASE}/api/tasks/x/follow/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: address }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) throw new Error(json?.error || "Follow verification failed");
      setXTaskStatus({
        loading: false,
        connected: true,
        completed: Boolean(json.completed),
        error: "",
      });
      if (json.completed) {
        setXFollowPendingVerify(false);
        setTaskCompletions((prev) => ({
          ...prev,
          x_follow_bobmonad: {
            completed: true,
            completedAt: json.completedAt || new Date().toISOString(),
            proof: { source: "x_verify" },
          },
        }));
        loadTaskCompletions(address);
        loadPointsLeaderboard();
      }
      return Boolean(json.completed);
    } catch (err) {
      setXTaskStatus((prev) => ({ ...prev, loading: false, error: "" }));
      return false;
    } finally {
      setXFollowBusy(false);
    }
  };

  const loadTaskCompletions = async (walletAddress = address) => {
    if (!walletAddress) {
      setTaskCompletions({});
      return;
    }
    const cacheKey = `task_completions:${String(walletAddress).toLowerCase()}`;
    try {
      const res = await fetch(
        `${X_TASKS_API_BASE}/api/tasks/completions?wallet=${encodeURIComponent(walletAddress)}`
      );
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) throw new Error(json?.error || "Completions unavailable");
      const next = json.completions || {};
      setTaskCompletions((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      writeDashboardCache(cacheKey, next);
    } catch {
      setTaskCompletions(readDashboardCache(cacheKey, {}));
    }
  };

  const loadPointsLeaderboard = async () => {
    try {
      const res = await fetch(`${X_TASKS_API_BASE}/api/tasks/leaderboard?limit=250`);
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok || !Array.isArray(json?.data)) throw new Error("Leaderboard unavailable");
      const next = json.data;
      setPointsLeaderboard((prev) => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
      writeDashboardCache("points_leaderboard", next);
    } catch {
      setPointsLeaderboard(readDashboardCache("points_leaderboard", []));
    }
  };

  const markTaskComplete = async (taskId) => {
    if (!address || !taskId) return;
    try {
      const res = await fetch(`${X_TASKS_API_BASE}/api/tasks/completions/mark`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: address, taskId }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) throw new Error(json?.error || "Failed to mark task");
      setTaskCompletions((prev) => ({
        ...prev,
        [taskId]: { completed: true, completedAt: json.completedAt || new Date().toISOString(), proof: null },
      }));
      loadTaskCompletions(address);
      loadPointsLeaderboard();
    } catch (err) {
      setXTaskStatus((prev) => ({ ...prev, error: "" }));
    }
  };

  const openTaskWindow = (taskId, taskUrl = "") => {
    const url = taskId === LIKE_RT_TASK_ID
      ? (latestTweetUrl || taskUrl || TASK_EXTERNAL_URLS[taskId] || DEFAULT_LATEST_TWEET_URL)
      : (taskUrl || TASK_EXTERNAL_URLS[taskId]);
    if (!url) return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const markTaskIntent = async (taskId) => {
    if (!address || !taskId) return null;
    try {
      const res = await fetch(`${X_TASKS_API_BASE}/api/tasks/intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ wallet: address, taskId }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) throw new Error(json?.error || "Failed to mark task intent");
      await loadTaskCompletions(address);
      return json;
    } catch {
      return null;
    }
  };

  const triggerHeaderWalletConnect = () => {
    const allButtons = Array.from(document.querySelectorAll("button"));
    const connectBtn = allButtons.find((btn) => {
      const label = String(btn.textContent || "").trim().toLowerCase();
      if (!label.includes("connect wallet")) return false;
      if (btn.hasAttribute("disabled")) return false;
      const style = window.getComputedStyle(btn);
      const hidden = style.display === "none" || style.visibility === "hidden" || style.opacity === "0";
      return !hidden;
    });
    if (connectBtn) {
      connectBtn.click();
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const openAndCompleteTask = async (taskId, taskUrl = "") => {
    await markTaskComplete(taskId);
    openTaskWindow(taskId, taskUrl);
  };

  const runTaskAction = async (taskId, fn) => {
    setTaskActionLoading((prev) => ({ ...prev, [taskId]: true }));
    try {
      await fn();
    } finally {
      setTaskActionLoading((prev) => ({ ...prev, [taskId]: false }));
    }
  };

  const connectedAddress = String(address || "").toLowerCase();
  const isDevConnected = Boolean(connectedAddress) && connectedAddress === DEV_TASKS_WALLET;
  const effectiveTaskCompletions = useMemo(() => {
    if (!isDevConnected || devTaskPreviewMode === "live") return taskCompletions;
    const map = {};
    taskCatalog.forEach((task) => {
      const completed = devTaskPreviewMode === "completed";
      map[task.id] = completed
        ? { completed: true, completedAt: new Date().toISOString(), proof: { source: "dev_preview" } }
        : { completed: false };
    });
    return map;
  }, [isDevConnected, devTaskPreviewMode, taskCompletions, taskCatalog]);

  const taskRowsWithStatus = taskCatalog
    .map((task, idx) => ({
      ...task,
      __idx: idx,
      completed: Boolean(effectiveTaskCompletions[task.id]?.completed),
    }))
    .sort((a, b) => {
      const ap = Object.prototype.hasOwnProperty.call(TASK_PRIORITY, a.id) ? TASK_PRIORITY[a.id] : 99;
      const bp = Object.prototype.hasOwnProperty.call(TASK_PRIORITY, b.id) ? TASK_PRIORITY[b.id] : 99;
      if (ap !== bp) return ap - bp;
      return a.__idx - b.__idx;
    });
  const availableTasks = taskRowsWithStatus.filter((task) => !task.completed);
  const completedTasks = taskRowsWithStatus.filter((task) => task.completed);
  const featuredTasks = [...availableTasks, ...completedTasks];
  const isXConnectCompleted = Boolean(effectiveTaskCompletions?.x_connect_twitter?.completed);

  const renderTaskActions = (task) => {
    const isTaskBusy = Boolean(taskActionLoading[task.id]);
    const isCapricornLiquidityTask =
      task.id === CAPRICORN_LP_TASK_ID ||
      (/capricorn/i.test(String(task?.title || "")) && /liquidity|lp/i.test(String(task?.title || "")));
    if (task.id === "daily_check_in") {
      if (task.completed) {
        return <span className="bd-task-completed-pill">Completed</span>;
      }
      return (
        <button
          type="button"
          className="bd-btn bd-btn-secondary bd-task-typo"
          onClick={() => runTaskAction(task.id, async () => markTaskComplete("daily_check_in"))}
          disabled={!address || isTaskBusy}
        >
          {isTaskBusy ? "Checking In..." : "Check In"}
        </button>
      );
    }
    if (task.completed) {
      return <span className="bd-task-completed-pill">Completed</span>;
    }
    if (task.id === "x_connect_twitter") {
      return (
        <button
          type="button"
          className="bd-btn bd-btn-secondary bd-task-typo"
          onClick={() =>
            runTaskAction(task.id, async () => {
              if (!xConnectPendingVerify) {
                setXConnectPendingVerify(true);
                savePendingVerifyState({
                  connect: true,
                  follow: xFollowPendingVerify,
                  likeRt: xLikeRtPendingVerify,
                });
                queueXConnectVerifyReset();
                await openXConnect();
                return;
              }
              const status = await refreshXFollowStatus(address, { silent: false });
              await loadTaskCompletions(address);
              await loadPointsLeaderboard();
              if (status?.connected) {
                setXConnectPendingVerify(false);
                savePendingVerifyState({
                  connect: false,
                  follow: xFollowPendingVerify,
                  likeRt: xLikeRtPendingVerify,
                });
              }
            })
          }
          disabled={xTaskStatus.loading || isTaskBusy || !address}
        >
          {(xTaskStatus.loading || isTaskBusy) ? "Verifying" : (xConnectPendingVerify ? "Verify" : "Connect X")}
        </button>
      );
    }
    if (task.id === "x_follow_bobmonad") {
      return (
        <button
          type="button"
          className="bd-btn bd-btn-secondary bd-task-typo"
          onClick={() =>
            runTaskAction(task.id, async () => {
              if (xFollowBusy) return;
              if (!xFollowPendingVerify) {
                setXFollowPendingVerify(true);
                savePendingVerifyState({
                  connect: xConnectPendingVerify,
                  follow: true,
                  likeRt: xLikeRtPendingVerify,
                });
                queueXFollowVerifyReset();
                openTaskWindow(task.id, task.url);
                return;
              }
              await verifyXFollow({ silent: false });
            })
          }
          disabled={!address || !isXConnectCompleted || xTaskStatus.loading || xFollowBusy || isTaskBusy}
        >
          {(xTaskStatus.loading || xFollowBusy || isTaskBusy) ? "Verifying" : (xFollowPendingVerify ? "Verify" : "Follow on X")}
        </button>
      );
    }
    if (task.id === LIKE_RT_TASK_ID) {
      return (
        <button
          type="button"
          className="bd-btn bd-btn-secondary bd-task-typo"
          onClick={() =>
            runTaskAction(task.id, async () => {
              if (!xLikeRtPendingVerify) {
                const latest = await loadLatestTweet();
                setXLikeRtPendingVerify(true);
                savePendingVerifyState({
                  connect: xConnectPendingVerify,
                  follow: xFollowPendingVerify,
                  likeRt: true,
                });
                queueXLikeRtVerifyReset();
                openTaskWindow(task.id, latest?.url || task.url);
                return;
              }
              await verifyXLikeRtLatest();
            })
          }
          disabled={!address || !isXConnectCompleted || xLikeRtBusy || isTaskBusy}
        >
          {(xLikeRtBusy || isTaskBusy) ? "Verifying" : (xLikeRtPendingVerify ? "Verify" : "Open Post")}
        </button>
      );
    }
    if (isCapricornLiquidityTask) {
      return (
        <button
          type="button"
          className="bd-btn bd-btn-secondary bd-task-typo"
          onClick={() =>
            runTaskAction(task.id, async () => {
              if (!capricornPendingVerify) {
                await markTaskIntent(task.id);
                setCapricornPendingVerify(true);
                queueCapricornVerifyReset();
                openTaskWindow(task.id, task.url);
                return;
              }
              await verifyCapricornLiquidity();
            })
          }
          disabled={!address || capricornVerifyBusy || isTaskBusy}
        >
          {(capricornVerifyBusy || isTaskBusy) ? "Verifying" : (capricornPendingVerify ? "Verify" : "Open Capricorn")}
        </button>
      );
    }
    if (task.id === "join_discord") {
      return (
        <button
          type="button"
          className="bd-btn bd-btn-secondary bd-task-typo"
          onClick={() => openAndCompleteTask("join_discord", task.url)}
          disabled={!address}
        >
          Join Discord
        </button>
      );
    }
    if (task.id === "join_telegram") {
      return (
        <button
          type="button"
          className="bd-btn bd-btn-secondary bd-task-typo"
          onClick={() => openAndCompleteTask("join_telegram", task.url)}
          disabled={!address}
        >
          Join Telegram
        </button>
      );
    }
    if (task.id === "visit_opensea") {
      return (
        <button
          type="button"
          className="bd-btn bd-btn-secondary bd-task-typo"
          onClick={() => openAndCompleteTask("visit_opensea", task.url)}
          disabled={!address}
        >
          Visit OpenSea
        </button>
      );
    }
    if (task.url) {
      return (
        <button
          type="button"
          className="bd-btn bd-btn-secondary bd-task-typo"
          onClick={() => openAndCompleteTask(task.id, task.url)}
          disabled={!address}
        >
          {String(task.buttonLabel || "").trim() || "Open Task"}
        </button>
      );
    }
    return null;
  };

  const loadTaskCatalog = async () => {
    try {
      const res = await fetch(`${X_TASKS_API_BASE}/api/tasks/catalog`);
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok || !Array.isArray(json?.data)) return;
      const normalized = json.data
        .map((t) => ({
          id: String(t?.id || "").trim(),
          title: String(t?.title || "").trim(),
          points: Number(t?.points || 0),
          url: String(t?.url || "").trim(),
          buttonLabel: String(t?.buttonLabel || "").trim(),
        }))
        .filter((t) => t.id && t.title && Number.isFinite(t.points) && t.points > 0);
      if (normalized.length) {
        setTaskCatalog(normalized);
        writeDashboardCache("task_catalog", normalized);
      }
    } catch {
      setTaskCatalog(readDashboardCache("task_catalog", COMMUNITY_TASK_ROWS));
    }
  };

  useEffect(() => {
    loadTaskCatalog();
    loadLatestTweet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const addTaskFromDev = async () => {
    const title = newTaskTitle.trim();
    const points = Number(newTaskPoints);
    const url = newTaskUrl.trim();
    const buttonLabel = newTaskButtonLabel.trim();
    if (!title || !Number.isFinite(points) || points <= 0) return;
    try {
      const res = await fetch(`${X_TASKS_API_BASE}/api/tasks/catalog/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ title, points, url, buttonLabel }),
      });
      const json = await res.json().catch(() => null);
      if (!res.ok || !json?.ok) return;
      if (Array.isArray(json.data)) {
        setTaskCatalog(json.data);
      } else {
        loadTaskCatalog();
      }
      setNewTaskTitle("");
      setNewTaskPoints("10");
      setNewTaskUrl("");
      setNewTaskButtonLabel("");
    } catch {}
  };

  const renderTaskInfo = (task) => {
    const rawTitle = String(task?.title || "").trim();
    const cleanTitle = task?.id === CAPRICORN_LP_TASK_ID
      ? "Add BOB Liquidity to Capricorn"
      : rawTitle.replace(/\s*\(\d+\s*pts\)\s*$/i, "");
    const pts = Number(task?.points || 0);
    return (
      <div>
        <strong>{Number.isFinite(pts) && pts > 0 ? `${cleanTitle} (${pts} pts)` : cleanTitle}</strong>
      </div>
    );
  };

  const renderTaskRow = (task, key) => (
    <div className="bd-community-task" key={key || task.id}>
      <span className={`task-dot ${task.completed ? "is-done" : ""}`} aria-hidden="true" />
      {renderTaskInfo(task)}
      <div className="bd-task-actions bd-task-actions-right">{renderTaskActions(task)}</div>
    </div>
  );

  const lifiWidgetConfig = useMemo(
    () => ({
      integrator: "bobmonad-dashboard",
      variant: "wide",
      appearance: "dark",
      fromChain: LI_FI_CHAIN_ID,
      toChain: LI_FI_CHAIN_ID,
      fromToken: "0x0000000000000000000000000000000000000000",
      toToken: BOB_TOKEN,
      fromAmount: safeQuoteAmount,
      useRecommendedRoute: true,
      hiddenUI: ["toAddress"],
      toAddress: address ? { address, chainType: "EVM", name: "Connected wallet" } : undefined,
      theme: {
        container: {
          border: "1px solid rgba(59, 130, 246, 0.3)",
          borderRadius: "12px",
          background: "rgba(2, 6, 23, 0.48)",
          height: "auto",
          maxHeight: "none",
        },
        routesContainer: {
          height: "auto",
          maxHeight: "none",
        },
        palette: {
          background: {
            default: "#020617",
            paper: "#020617",
          },
          text: {
            primary: "#e7edff",
            secondary: "#bfdbfe",
          },
        },
      },
    }),
    [address, safeQuoteAmount]
  );

  const lifiBridgeWidgetConfig = useMemo(
    () => ({
      integrator: "bobmonad-dashboard",
      variant: "wide",
      appearance: "dark",
      fromChain: BASE_CHAIN_ID,
      toChain: LI_FI_CHAIN_ID,
      fromToken: BASE_NATIVE,
      toToken: BOB_TOKEN,
      useRecommendedRoute: true,
      hiddenUI: ["toAddress"],
      toAddress: address ? { address, chainType: "EVM", name: "Connected wallet" } : undefined,
      theme: {
        container: {
          border: "1px solid rgba(59, 130, 246, 0.3)",
          borderRadius: "12px",
          background: "rgba(2, 6, 23, 0.48)",
          height: "auto",
          maxHeight: "none",
        },
        routesContainer: {
          height: "auto",
          maxHeight: "none",
        },
        palette: {
          background: {
            default: "#020617",
            paper: "#020617",
          },
          text: {
            primary: "#e7edff",
            secondary: "#bfdbfe",
          },
        },
      },
    }),
    [address]
  );

  const AnalyticsLiveContent = () => (
    <>
      <section className="bd-card bd-hero bd-header-stack">
        <p className="bd-kicker">LIVE STATS</p>
        <h1>THE <span className="bd-accent">BOB</span> ECONOMY</h1>
        <p>Fresh market data, holder distribution, and live charting in one command center.</p>
        <div className="bd-stats-grid">
          <div className="bd-stat"><span>Market cap</span><strong>{fmtUsdWhole(Number(pairData?.marketCap || 0))}</strong><em>Live</em></div>
          <div className="bd-stat"><span>Holders</span><strong>{fmtNum(holderCount, 0)}</strong><em>Boblievers</em></div>
          <div className="bd-stat"><span>Volume (24h)</span><strong>{fmtUsdWhole(Number(pairData?.volume?.h24 || 0))}</strong><em>Dex flow</em></div>
          <div className="bd-stat">
            <span>24h Change</span>
            <strong
              className={
                Number(pairData?.priceChange?.h24 || 0) > 0
                  ? "bd-value-positive"
                  : Number(pairData?.priceChange?.h24 || 0) < 0
                    ? "bd-value-negative"
                    : ""
              }
            >
              {Number(pairData?.priceChange?.h24 || 0).toFixed(2)}%
            </strong>
            <em>Price move</em>
          </div>
        </div>
        <AnalyticsPriceChart compact={compactChart} apiBase={DASHBOARD_API_BASE} />
      </section>

      <section className="bd-card">
        <div id="holders-leaderboard" />
        <div className="bd-head-row">
            <div className="bd-holders-head-main bd-header-stack">
              <p className="bd-kicker">Rankings</p>
              <h1>HOLDERS <span className="bd-accent">LEADERBOARD</span></h1>
              <p>Live top holder balances with LP wallets explicitly labeled.</p>
            </div>
          <div className="bd-mini-stats bd-mini-stats-holders">
            <div><span>Holders</span><strong>{fmtNum(holderCount, 0)}</strong></div>
            <div><span>Leaderboard</span><strong>Top 100</strong></div>
          </div>
        </div>
        {holdersError ? <p className="bd-help">{holdersError}</p> : null}
        <div className="bd-table-wrap">
          <table className="bd-table">
            <thead><tr><th>Rank</th><th>Wallet</th><th>$BOB Balance</th><th>Badge</th><th></th></tr></thead>
            <tbody>
              {pageRows.map((r, idx) => {
                const rank = (holderPage - 1) * perPage + idx + 1;
                const wallet = String(r.wallet || "").toLowerCase();
                const nonLpRank = Number(nonLpRankMap.get(wallet) || 0);
                const badgeMeta = getHolderBadgeMeta(wallet, nonLpRank);
                return (
                  <tr key={`${r.wallet}-${rank}`}>
                    <td><span className="bd-rank-chip">#{rank}</span></td>
                    <td>{ultraCompactWallets ? shortenTiny(r.wallet) : (compactChart ? shortenCompact(r.wallet) : shorten(r.wallet))}</td>
                    <td>
                      <span className="bd-balance-val">
                        {fmtBobCompact(r.balance)}
                        {" "}
                        <span className="bd-balance-sub">
                          ({((r.balance / BOB_TOTAL_SUPPLY) * 100).toFixed(2)}%)
                        </span>
                      </span>
                    </td>
                    <td className="bd-badge-cell"><span className={`bd-badge-chip ${badgeMeta.className}`}>{badgeMeta.label}</span></td>
                    <td className="bd-explorer-cell">
                      <a
                        className="bd-holder-explorer-link"
                        href={`${HOLDER_EXPLORER_BASE}/${r.wallet}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`View ${r.wallet} on explorer`}
                        title="View on Explorer"
                      >
                        <svg viewBox="0 0 24 24" aria-hidden="true">
                          <path d="M14 5h5v5" />
                          <path d="M10 14L19 5" />
                          <path d="M19 14v5h-14v-14h5" />
                        </svg>
                      </a>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="bd-pager">
          <button type="button" className="bd-btn bd-btn-secondary" onClick={() => setHolderPage((p) => Math.max(1, p - 1))} disabled={holderPage <= 1}>Previous</button>
          <button type="button" className="bd-btn bd-btn-secondary" onClick={() => setHolderPage((p) => Math.min(pages, p + 1))} disabled={holderPage >= pages}>Next</button>
        </div>
      </section>
    </>
  );

  const InventoryContent = () => (
    <>
    <section className="bd-card bd-inventory-section">
      <div className="bd-head-row">
        <div className="bd-holders-head-main bd-header-stack">
          <p className="bd-kicker">Token Inventory</p>
          <h1>YOUR <span className="bd-accent">$BOB</span> TOKEN POSITION</h1>
          <p>Wallet-level token snapshot with live value context.</p>
        </div>
      </div>
        <div className={`bd-inventory-token-wrap${!address ? " is-locked" : ""}`}>
          <div className="bd-inventory-token-content">
        <div className="bd-stats-grid">
          <div className="bd-stat">
            <span>Balance</span>
            <strong>{address && !bobBalanceLoading && inventoryBalanceLabel !== "-" ? `${inventoryBalanceLabel} BOB` : inventoryBalanceLabel}</strong>
            <em>{inventorySupplyLabel}</em>
          </div>
          <div className="bd-stat">
            <span>USD Value</span>
            <strong>{inventoryUsdValue}</strong>
            <em>Estimated</em>
          </div>
          <div className="bd-stat">
            <span>24h Change</span>
            <strong
              className={
                Number(pairData?.priceChange?.h24 || 0) > 0
                  ? "bd-value-positive"
                  : Number(pairData?.priceChange?.h24 || 0) < 0
                    ? "bd-value-negative"
                    : ""
              }
            >
              {Number(pairData?.priceChange?.h24 || 0).toFixed(2)}%
            </strong>
            <em>Live</em>
          </div>
          <div className="bd-stat">
            <span>Title</span>
            <strong className="bd-stat-chip-value">
              <span
                className={`bd-badge-chip bd-badge-chip--compact bd-inventory-label-chip ${address ? inventoryBadgeMeta.className : "is-base"}`}
                style={{ height: "24px", minHeight: "24px" }}
              >
                <span
                  style={{
                    all: "unset",
                    display: "inline-block",
                    fontSize: "16px",
                    lineHeight: "1",
                    fontWeight: 800,
                    fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                    letterSpacing: "0.05em",
                    textTransform: "uppercase",
                    color: "inherit",
                  }}
                >
                  {address ? inventoryBadgeMeta.label : "-"}
                </span>
              </span>
            </strong>
            <em>Holder tier</em>
          </div>
        </div>
        <AnalyticsPriceChart
          compact={compactChart}
          apiBase={DASHBOARD_API_BASE}
          supplyMultiplier={inventoryBobBalance}
          highLabel="24H High Value"
          lowLabel="24H Low Value"
          ctaLabel="Open market chart"
          showCta={false}
        />
          </div>
          {!address ? (
            <div className="bd-tasks-lock-overlay bd-inventory-lock-overlay" role="status" aria-live="polite">
              <div className="bd-tasks-lock-card">
                <span className="bd-tasks-lock-kicker">Wallet Required</span>
                <div className="bd-tasks-lock-title">
                  Unlock <span className="bd-accent">Token Inventory</span>
                </div>
                <div className="bd-tasks-lock-copy">
                  Connect your wallet to view live balance, value, and title stats.
                </div>
                <button
                  type="button"
                  className="bd-btn bd-btn-secondary bd-task-typo"
                  onClick={triggerHeaderWalletConnect}
                >
                  Connect Wallet
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </section>
      <section className="bd-card bd-inventory-section bd-inventory-section--nft">
        <div className="bd-head-row">
          <div className="bd-holders-head-main bd-header-stack">
            <p className="bd-kicker">NFT Inventory</p>
            <h1>YOUR <span className="bd-accent">BOB NFT</span> COLLECTION</h1>
            <p>Wallet-level NFT snapshot with collection and trait insights.</p>
          </div>
        </div>
        <div className="bd-inventory-placeholder-card" role="status" aria-live="polite">
          <span className="bd-inventory-placeholder-kicker">Coming Soon</span>
          <div className="bd-inventory-placeholder-copy">
            Your NFT collection will appear here once it goes live.
          </div>
        </div>
      </section>
    </>
    );

  const TasksContent = () => {
    return (
      <>
          <section className="bd-community-panel bd-community-panel-main bd-tasks-panel bd-header-stack">
            <div className="bd-holders-head-main bd-header-stack">
              <p className="bd-kicker">Missions</p>
              <h1>ACTIVE <span className="bd-accent">TASKS</span></h1>
              <p>Complete available tasks to stack points and boost your leaderboard position.</p>
            </div>
            <div className={`bd-community-task-list-wrap${!address ? " is-locked" : ""}`}>
            <div className="bd-community-task-list">
              {featuredTasks.map((task) => (
                renderTaskRow(task, task.id)
              ))}
            </div>
            {!address ? (
              <div className="bd-tasks-lock-overlay" role="status" aria-live="polite">
                <div className="bd-tasks-lock-card">
                  <span className="bd-tasks-lock-kicker">Wallet Required</span>
                  <div className="bd-tasks-lock-title">
                    Unlock <span className="bd-accent">Missions</span>
                  </div>
                  <div className="bd-tasks-lock-copy">
                    Connect your wallet to activate task actions and start earning points.
                  </div>
                  <button
                    type="button"
                    className="bd-btn bd-btn-secondary bd-task-typo"
                    onClick={triggerHeaderWalletConnect}
                  >
                    Connect Wallet
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <section className="bd-community-panel bd-community-panel-main bd-community-panel--leaderboard">
          <div className="bd-holders-head-main bd-header-stack">
            <p className="bd-kicker">Leaderboard</p>
            <h1>POINTS <span className="bd-accent">RANKING</span></h1>
            <p>Live ranking based on completed community tasks and points earned.</p>
          </div>
          {address ? (
            <div className="bd-community-lb">
              <div className="lb-row">
                <span>#{personalRow.rank}</span>
                <strong>{personalRow.name}</strong>
                <em>{personalRow.points}</em>
              </div>
            </div>
          ) : null}
          <div className="bd-community-lb">
            {leaderboardRows.map((row, i) => (
              <div className="lb-row" key={`${row?.wallet || row?.username || "none"}-${i}`}>
                <span>#{row?.rank || i + 1}</span>
                <strong>
                  {row
                    ? (row.username
                      ? `@${String(row.username).replace(/^@/, "")}`
                      : (ultraCompactWallets ? shortenTiny(row.wallet) : (compactChart ? shortenCompact(row.wallet) : shorten(row.wallet))))
                    : "None"}
                </strong>
                <em>{row ? `${fmtNum(row.points, 0)} pts` : "-"}</em>
              </div>
            ))}
          </div>
        </section>
      </>
    );
  };

  const CommunityContent = () => {
    const [memeIndex, setMemeIndex] = useState(0);
    const [eventDrawIndex, setEventDrawIndex] = useState(0);
    const [eventStageBooted, setEventStageBooted] = useState(false);
    const [memeRenderIndexA, setMemeRenderIndexA] = useState(0);
    const [memeRenderIndexB, setMemeRenderIndexB] = useState(0);
    const [eventRenderIndexA, setEventRenderIndexA] = useState(0);
    const [eventRenderIndexB, setEventRenderIndexB] = useState(0);
    const [memeActiveLayer, setMemeActiveLayer] = useState("a");
    const [eventActiveLayer, setEventActiveLayer] = useState("a");
    const [selectedMemeFileName, setSelectedMemeFileName] = useState("");
    const [openCreatorId, setOpenCreatorId] = useState("");
    const [isSongPlaying, setIsSongPlaying] = useState(false);
    const [songProgress, setSongProgress] = useState(0);
    const [songDuration, setSongDuration] = useState(0);
    const [songVolume, setSongVolume] = useState(0.8);
    const [showVolumeControl, setShowVolumeControl] = useState(false);
    const [creatorAssetsReady, setCreatorAssetsReady] = useState(false);
    const [eventNowTick, setEventNowTick] = useState(() => Date.now());
    const memeUploadInputRef = useRef(null);
    const songAudioRef = useRef(null);
    const tweetEmbedRef = useRef(null);
    const memeReadyRef = useRef(new Set());
    const memeDecodePromisesRef = useRef(new Map());
    const eventDecodePromisesRef = useRef(new Map());
    const memeTransitionReqRef = useRef(0);
    const eventTransitionReqRef = useRef(0);
    const memeIndexRef = useRef(0);
    const eventIndexRef = useRef(0);
    const memeTransitioningRef = useRef(false);
    const eventTransitioningRef = useRef(false);
    const memeLayerRef = useRef("a");
    const eventLayerRef = useRef("a");
    const eventReadyRef = useRef(new Set());

    const ensureMemeReady = (src) => {
      if (!src) return Promise.resolve();
      if (memeReadyRef.current.has(src)) return Promise.resolve();
      const pending = memeDecodePromisesRef.current.get(src);
      if (pending) return pending;
      const img = new Image();
      img.src = src;
      const p = new Promise((resolve) => {
        const done = () => {
          memeReadyRef.current.add(src);
          memeDecodePromisesRef.current.delete(src);
          resolve();
        };
        if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
          done();
          return;
        }
        img.onload = done;
        img.onerror = done;
        if (typeof img.decode === "function") {
          img.decode().then(done).catch(() => {});
        }
      });
      memeDecodePromisesRef.current.set(src, p);
      return p;
    };

    const setMemeIndexWhenReady = (idx) => {
      if (!Number.isFinite(idx) || idx < 0 || idx >= COMMUNITY_MEMES.length) return;
      const current = memeIndexRef.current;
      if (idx === current || memeTransitioningRef.current) return;
      memeTransitioningRef.current = true;
      const reqId = ++memeTransitionReqRef.current;
      ensureMemeReady(COMMUNITY_MEMES[idx]).then(() => {
        if (memeTransitionReqRef.current !== reqId) return;
        if (memeLayerRef.current === "a") {
          setMemeRenderIndexB(idx);
          setMemeActiveLayer("b");
          memeLayerRef.current = "b";
        } else {
          setMemeRenderIndexA(idx);
          setMemeActiveLayer("a");
          memeLayerRef.current = "a";
        }
        setMemeIndex(idx);
        window.setTimeout(() => {
          memeTransitioningRef.current = false;
        }, 560);
      });
    };

    const ensureEventReady = (src) => {
      if (!src) return Promise.resolve();
      if (eventReadyRef.current.has(src)) return Promise.resolve();
      const pending = eventDecodePromisesRef.current.get(src);
      if (pending) return pending;
      const img = new Image();
      img.src = src;
      const p = new Promise((resolve) => {
        const done = () => {
          eventReadyRef.current.add(src);
          eventDecodePromisesRef.current.delete(src);
          resolve();
        };
        if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) {
          done();
          return;
        }
        img.onload = done;
        img.onerror = done;
        if (typeof img.decode === "function") {
          img.decode().then(done).catch(() => {});
        }
      });
      eventDecodePromisesRef.current.set(src, p);
      return p;
    };

    const setEventIndexWhenReady = (idx) => {
      if (!Number.isFinite(idx) || idx < 0 || idx >= FEATURED_EVENT_DRAWS.length) return;
      const current = eventIndexRef.current;
      if (idx === current || eventTransitioningRef.current) return;
      eventTransitioningRef.current = true;
      const reqId = ++eventTransitionReqRef.current;
      const src = FEATURED_EVENT_DRAWS[idx]?.image;
      ensureEventReady(src).then(() => {
        if (eventTransitionReqRef.current !== reqId) return;
        if (eventLayerRef.current === "a") {
          setEventRenderIndexB(idx);
          setEventActiveLayer("b");
          eventLayerRef.current = "b";
        } else {
          setEventRenderIndexA(idx);
          setEventActiveLayer("a");
          eventLayerRef.current = "a";
        }
        setEventDrawIndex(idx);
        window.setTimeout(() => {
          eventTransitioningRef.current = false;
        }, EVENT_DRAW_FADE_MS + 40);
      });
    };

    useEffect(() => {
      COMMUNITY_MEMES.forEach((src) => { ensureMemeReady(src); });
    }, []);

    useEffect(() => {
      memeIndexRef.current = memeIndex;
    }, [memeIndex]);

    useEffect(() => {
      eventIndexRef.current = eventDrawIndex;
    }, [eventDrawIndex]);

    useEffect(() => {
      let cancelled = false;
      setEventStageBooted(false);
      Promise.all(FEATURED_EVENT_DRAWS.map((it) => ensureEventReady(it?.image))).finally(() => {
        if (cancelled) return;
        setEventRenderIndexA(0);
        setEventRenderIndexB(0);
        setEventActiveLayer("a");
        eventLayerRef.current = "a";
        setEventStageBooted(true);
      });
      return () => {
        cancelled = true;
      };
    }, []);

    useEffect(() => {
      let cancelled = false;
      setCreatorAssetsReady(false);
      const creatorImgs = COMMUNITY_CREATOR_ITEMS
        .map((item) => String(item?.image || "").trim())
        .filter(Boolean);
      Promise.all(
        creatorImgs.map((src) => {
          const img = new Image();
          img.src = src;
          if (img.complete && img.naturalWidth > 0 && img.naturalHeight > 0) return Promise.resolve();
          return new Promise((resolve) => {
            img.onload = () => resolve();
            img.onerror = () => resolve();
            if (typeof img.decode === "function") {
              img.decode().then(resolve).catch(() => {});
            }
          });
        })
      )
        .catch(() => {})
        .finally(() => {
          if (cancelled) return;
          window.requestAnimationFrame(() => {
            if (!cancelled) setCreatorAssetsReady(true);
          });
        });
      return () => {
        cancelled = true;
      };
    }, []);

    useEffect(() => {
      const id = setInterval(() => {
        const next = (memeIndexRef.current + 1) % COMMUNITY_MEMES.length;
        setMemeIndexWhenReady(next);
      }, 10_000);
      return () => clearInterval(id);
    }, []);

    useEffect(() => {
      if (!eventStageBooted || FEATURED_EVENT_DRAWS.length <= 1) return undefined;
      const id = setInterval(() => {
        if (eventTransitioningRef.current) return;
        const next = (eventIndexRef.current + 1) % FEATURED_EVENT_DRAWS.length;
        setEventIndexWhenReady(next);
      }, EVENT_DRAW_ROTATE_MS);
      return () => clearInterval(id);
    }, [eventStageBooted]);

    useEffect(() => {
      const id = setInterval(() => setEventNowTick(Date.now()), 1000);
      return () => clearInterval(id);
    }, []);

    const onSelectMemeSlide = (idx) => {
      setMemeIndexWhenReady(idx);
    };

    const onPickMemeFile = () => {
      memeUploadInputRef.current?.click();
    };

    const onMemeFileChanged = (event) => {
      const file = event?.target?.files?.[0];
      setSelectedMemeFileName(file?.name ? String(file.name) : "");
    };

    const onShareMemeToX = () => {
      const tweetText = selectedMemeFileName
        ? `New BOB meme drop: ${selectedMemeFileName} #BobMonad @BobMonad`
        : "New BOB meme drop. #BobMonad @BobMonad";
      window.open(`https://x.com/intent/tweet?text=${encodeURIComponent(tweetText)}`, "_blank", "noopener,noreferrer");
    };

    const onDownloadMeme = () => {
      const src = String(currentMeme || "").trim();
      if (!src) return;
      const link = document.createElement("a");
      link.href = src;
      link.download = src.split("/").pop() || "bob-meme";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    };

    const openLatestBobTweet = () => {
      const tweet = String(latestTweetUrl || "").trim() || DEFAULT_LATEST_TWEET_URL;
      window.open(tweet, "_blank", "noopener,noreferrer");
    };

    const effectiveTweetId = latestTweetId
      ? String(latestTweetId)
      : (String(latestTweetUrl || "").match(/status\/(\d+)/i)?.[1] || "");

    const onToggleSong = (itemId) => {
      const nextOpen = openCreatorId === itemId ? "" : itemId;
      setOpenCreatorId(nextOpen);
      setShowVolumeControl(false);
      window.setTimeout(() => {
        if (!songAudioRef.current) return;
        if (nextOpen === itemId) {
          songAudioRef.current.volume = songVolume;
          songAudioRef.current.currentTime = 0;
          songAudioRef.current.play().catch(() => {});
        } else {
          songAudioRef.current.pause();
        }
      }, 0);
    };

    const onToggleSongPlayback = () => {
      if (!songAudioRef.current) return;
      if (songAudioRef.current.paused) {
        songAudioRef.current.play().catch(() => {});
      } else {
        songAudioRef.current.pause();
      }
    };

    const onSeekSong = (nextPct) => {
      if (!songAudioRef.current || !Number.isFinite(songDuration) || songDuration <= 0) return;
      const pct = Math.max(0, Math.min(100, Number(nextPct)));
      songAudioRef.current.currentTime = (pct / 100) * songDuration;
      setSongProgress(pct);
    };

    useEffect(() => {
      const el = songAudioRef.current;
      if (!el) return;
      const onPlay = () => setIsSongPlaying(true);
      const onPause = () => setIsSongPlaying(false);
      const onEnded = () => {
        setIsSongPlaying(false);
        setSongProgress(0);
      };
      const onTimeUpdate = () => {
        if (!Number.isFinite(el.duration) || el.duration <= 0) return;
        setSongDuration(el.duration);
        setSongProgress((el.currentTime / el.duration) * 100);
      };
      const onLoaded = () => {
        if (!Number.isFinite(el.duration) || el.duration <= 0) return;
        setSongDuration(el.duration);
      };
      el.addEventListener("play", onPlay);
      el.addEventListener("pause", onPause);
      el.addEventListener("ended", onEnded);
      el.addEventListener("timeupdate", onTimeUpdate);
      el.addEventListener("loadedmetadata", onLoaded);
      return () => {
        el.removeEventListener("play", onPlay);
        el.removeEventListener("pause", onPause);
        el.removeEventListener("ended", onEnded);
        el.removeEventListener("timeupdate", onTimeUpdate);
        el.removeEventListener("loadedmetadata", onLoaded);
      };
    }, [openCreatorId]);

    useEffect(() => {
      if (!effectiveTweetId || !tweetEmbedRef.current) return;
      const host = tweetEmbedRef.current;
      host.innerHTML = `<blockquote class="twitter-tweet" data-theme="dark" data-dnt="true"><a href="https://x.com/BobMonad/status/${effectiveTweetId}"></a></blockquote>`;

      const loadWidget = () => {
        if (window?.twttr?.widgets?.load) {
          window.twttr.widgets.load(host);
        }
      };

      if (window?.twttr?.widgets?.load) {
        loadWidget();
      } else if (!document.getElementById("twitter-wjs")) {
        const s = document.createElement("script");
        s.id = "twitter-wjs";
        s.async = true;
        s.src = "https://platform.twitter.com/widgets.js";
        s.onload = loadWidget;
        document.body.appendChild(s);
      }

      return () => {
      };
    }, [effectiveTweetId]);

    const currentMeme = COMMUNITY_MEMES[memeIndex];
    const eventLayerDrawA = FEATURED_EVENT_DRAWS[eventRenderIndexA] || FEATURED_EVENT_DRAWS[0];
    const eventLayerDrawB = FEATURED_EVENT_DRAWS[eventRenderIndexB] || FEATURED_EVENT_DRAWS[0];
    const eventClock = getEventCountdownState(new Date(eventNowTick));
    const memeLayerSrcA = COMMUNITY_MEMES[memeRenderIndexA] || COMMUNITY_MEMES[0];
    const memeLayerSrcB = COMMUNITY_MEMES[memeRenderIndexB] || COMMUNITY_MEMES[0];
    return (
      <>
        <section className="bd-card bd-community-shell bd-community-shell--events bd-community-block">
          <div className="bd-event-layout">
            <div className="bd-event-copy-col bd-holders-head-main bd-header-stack">
              <p className="bd-kicker">Featured Events</p>
              <h1>WEEKLY <span className="bd-accent">BOB DRAWS</span></h1>
              <div className="bd-event-subcard">
                <p className="bd-community-sub">
                  Every Wednesday in Discord, our resident Bob artist Dio hosts live 1-of-1 Bob Draw sessions for the community from 2PM to 4PM EST.
                  <br />
                  <br />
                  Join us live, hang with the community, and watch a fresh custom Bob piece come to life each week.
                </p>
                <div className="bd-event-cta-row">
                  <div className="bd-event-status-row">
                    <span className={`bd-event-dot ${eventClock.isLive ? "is-live" : "is-upcoming"}`} aria-hidden="true" />
                    <span className="bd-event-status-label">Next Event:</span>
                    <strong className="bd-event-status-time">
                      {eventClock.isLive ? "Live" : eventClock.countdown}
                    </strong>
                  </div>
                  <a
                    className="bd-btn bd-btn-primary bd-event-discord-btn"
                    href="https://discord.gg/c69kaPBcav"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Visit Discord
                  </a>
                </div>
              </div>
            </div>
            <div className="bd-event-draws-stage">
              <div className="bd-event-draws-image-wrap">
                {!eventStageBooted ? <div className="bd-event-draws-skeleton" /> : null}
                <div className={`bd-event-draw-layer ${eventActiveLayer === "a" ? "is-active" : ""}`}>
                  <img
                    className="bd-event-draws-image"
                    src={eventLayerDrawA.image}
                    alt="Latest Bob Draw winner art"
                  />
                  <div className="bd-event-draws-winner-strip">
                    <span className="bd-event-winner-label">Latest Winner:</span>
                    <span className="bd-event-winner-name">{eventLayerDrawA.winner}</span>
                  </div>
                </div>
                <div className={`bd-event-draw-layer ${eventActiveLayer === "b" ? "is-active" : ""}`}>
                  <img
                    className="bd-event-draws-image"
                    src={eventLayerDrawB.image}
                    alt="Latest Bob Draw winner art"
                  />
                  <div className="bd-event-draws-winner-strip">
                    <span className="bd-event-winner-label">Latest Winner:</span>
                    <span className="bd-event-winner-name">{eventLayerDrawB.winner}</span>
                  </div>
                </div>
                <div className="bd-event-draws-glow" />
              </div>
            </div>
          </div>
        </section>

        <section className={`bd-card bd-community-shell bd-community-shell--creator bd-community-block ${creatorAssetsReady ? "is-ready" : "is-initializing"}`}>
            <div className="bd-holders-head-main bd-header-stack">
              <p className="bd-kicker">Creator Spotlight</p>
              <h1>COMMUNITY <span className="bd-accent">BUILDS</span></h1>
              <p>Featured apps, tools, and media shipped by BOB builders.</p>
            </div>
            <div className="bd-creator-grid">
              {COMMUNITY_CREATOR_ITEMS.map((item) => (
                <article className="bd-creator-card" key={item.id}>
                  <div className="bd-creator-image-wrap">
                    <img src={item.image} alt={`${item.title} by ${item.creator}`} />
                    <div className="bd-creator-overlay">
                      <p className="bd-creator-type bd-creator-type-chip">{item.type}</p>
                      <div className="bd-creator-overlay-foot">
                        <div className="bd-creator-overlay-meta-row">
                          <h5>{item.title}</h5>
                          <p className="bd-creator-meta">by @{item.creator}</p>
                        </div>
                        <p className="bd-creator-desc">{item.description}</p>
                      </div>
                    </div>
                  </div>
                  <div className="bd-creator-copy">
                    {item.audio ? (
                      <>
                        {openCreatorId === item.id ? (
                          <div className="bd-creator-audio-wrap">
                            <audio
                              ref={songAudioRef}
                              preload="metadata"
                              src={item.audio}
                              className="bd-creator-audio-hidden"
                            >
                              Your browser does not support audio playback.
                            </audio>
                            <div className="bd-creator-player-row">
                              <button
                                type="button"
                                className="bd-btn bd-btn-primary bd-creator-player-btn"
                                onClick={onToggleSongPlayback}
                              >
                                {isSongPlaying ? "Pause" : "Play"}
                              </button>
                              <label className="bd-creator-player-range bd-creator-player-range--track">
                                <span className="sr-only">Track</span>
                                <input
                                  type="range"
                                  min="0"
                                  max="100"
                                  step="0.1"
                                  value={songProgress}
                                  onChange={(e) => onSeekSong(e.target.value)}
                                />
                              </label>
                              <div className="bd-creator-volume-wrap">
                                <button
                                  type="button"
                                  className="bd-btn bd-btn-secondary bd-creator-volume-btn"
                                  onClick={() => setShowVolumeControl((v) => !v)}
                                  aria-label="Adjust volume"
                                >
                                  Vol
                                </button>
                                {showVolumeControl ? (
                                  <div className="bd-creator-volume-pop">
                                    <input
                                      type="range"
                                      min="0"
                                      max="1"
                                      step="0.01"
                                      value={songVolume}
                                      onChange={(e) => {
                                        const next = Math.max(0, Math.min(1, Number(e.target.value)));
                                        setSongVolume(next);
                                        if (songAudioRef.current) songAudioRef.current.volume = next;
                                      }}
                                    />
                                  </div>
                                ) : null}
                              </div>
                            </div>
                          </div>
                        ) : (
                          <button
                            type="button"
                            className="bd-btn bd-btn-secondary bd-creator-link"
                            onClick={() => onToggleSong(item.id)}
                          >
                            Open
                          </button>
                        )}
                      </>
                    ) : null}
                    {!item.audio && item.href ? (
                      <a
                        className="bd-btn bd-btn-secondary bd-creator-link"
                        href={item.href}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Open
                      </a>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
        </section>

        <div className="bd-community-media-row-clean">
            <div className="bd-community-media-col-left">
            <section className="bd-card bd-community-media-card bd-community-media-card--meme">
            <div className="bd-holders-head-main bd-header-stack">
              <p className="bd-kicker">Meme Wall</p>
              <h1>FEATURED <span className="bd-accent">MEMES</span></h1>
              <p>Fresh community drops from the Bob Army, ready to share and amplify.</p>
            </div>
            <div className="bd-clean-meme-middle">
              <div className="bd-clean-meme-stage">
                <div
                  className={`bd-meme-layer bd-meme-layer-a ${memeActiveLayer === "a" ? "is-active" : ""}`}
                  aria-hidden={memeActiveLayer !== "a"}
                >
                  <div className="bd-meme-layer-bg">
                    <img src={memeLayerSrcA} alt="" loading="eager" decoding="async" draggable="false" />
                  </div>
                  <div className="bd-meme-layer-fg">
                    <img src={memeLayerSrcA} alt="" loading="eager" decoding="async" draggable="false" />
                  </div>
                </div>
                <div
                  className={`bd-meme-layer bd-meme-layer-b ${memeActiveLayer === "b" ? "is-active" : ""}`}
                  aria-hidden={memeActiveLayer !== "b"}
                >
                  <div className="bd-meme-layer-bg">
                    <img src={memeLayerSrcB} alt="" loading="eager" decoding="async" draggable="false" />
                  </div>
                  <div className="bd-meme-layer-fg">
                    <img src={memeLayerSrcB} alt="" loading="eager" decoding="async" draggable="false" />
                  </div>
                </div>
                <div className="bd-meme-preload-strip" aria-hidden="true">
                  {COMMUNITY_MEMES.map((meme) => (
                    <img key={meme} src={meme} alt="" loading="eager" decoding="async" draggable="false" />
                  ))}
                </div>
              </div>
              <div className="bd-clean-meme-dots" role="tablist" aria-label="Meme slides">
                {COMMUNITY_MEMES.map((meme, i) => (
                  <button
                    type="button"
                    key={meme}
                    className={i === memeIndex ? "is-active" : ""}
                    onClick={() => onSelectMemeSlide(i)}
                    aria-label={`Show meme ${i + 1}`}
                  />
                ))}
              </div>
            </div>
            <div className="bd-clean-meme-actions-row">
              <button type="button" className="bd-btn bd-btn-secondary bd-meme-action-btn" onClick={onPickMemeFile}>
                <span
                  className="bd-meme-action-icon"
                  style={{ "--meme-action-icon-url": `url(${process.env.PUBLIC_URL || ""}/icons/upload.svg)` }}
                  aria-hidden="true"
                />
                <span>Upload</span>
              </button>
              <button type="button" className="bd-btn bd-btn-secondary bd-meme-action-btn">
                <span
                  className="bd-meme-action-icon"
                  style={{ "--meme-action-icon-url": `url(${process.env.PUBLIC_URL || ""}/icons/generate.svg)` }}
                  aria-hidden="true"
                />
                <span>Generate</span>
              </button>
              <button type="button" className="bd-btn bd-btn-secondary bd-meme-action-btn" onClick={onDownloadMeme}>
                <span
                  className="bd-meme-action-icon"
                  style={{ "--meme-action-icon-url": `url(${process.env.PUBLIC_URL || ""}/icons/download.svg)` }}
                  aria-hidden="true"
                />
                <span>Download</span>
              </button>
              <button type="button" className="bd-btn bd-btn-secondary bd-meme-action-btn" onClick={onShareMemeToX}>
                <span
                  className="bd-meme-action-icon bd-meme-action-icon--share"
                  style={{ "--meme-action-icon-url": `url(${process.env.PUBLIC_URL || ""}/icons/share.svg)` }}
                  aria-hidden="true"
                />
                <span>Share</span>
              </button>
            </div>
            <input
              ref={memeUploadInputRef}
              className="bd-hidden-file-input"
              type="file"
              accept="image/*"
              onChange={onMemeFileChanged}
            />
            {selectedMemeFileName ? (
              <p className="bd-meme-upload-note">Selected: {selectedMemeFileName}</p>
            ) : null}
          </section>

          <section className="bd-card bd-community-media-card bd-community-media-card--empty" aria-label="Reserved community section" />
          </div>

          <section className="bd-card bd-community-media-card bd-community-media-card--tweet">
            <div className="bd-holders-head-main bd-header-stack">
              <p className="bd-kicker">Social Pulse</p>
              <h1>LATEST <span className="bd-accent">UPDATE</span></h1>
              <p>See the newest post from Bob and jump straight into the live thread.</p>
            </div>
            {effectiveTweetId ? (
              <div className="bd-clean-tweet-embed-wrap" ref={tweetEmbedRef} />
            ) : (
              <div className="bd-latest-tweet-card">
                <p>Current post</p>
                <a href={latestTweetUrl || DEFAULT_LATEST_TWEET_URL} target="_blank" rel="noreferrer">
                  Open latest Bob post
                </a>
              </div>
            )}
            <button type="button" className="bd-btn bd-btn-primary bd-latest-tweet-btn" onClick={openLatestBobTweet}>
              View Tweet
            </button>
          </section>
        </div>

      </>
    );
  };
  useEffect(() => {
    if (!isDevConnected && showDevConsole) {
      setShowDevConsole(false);
      setDevTaskPreviewMode("live");
    }
  }, [isDevConnected, showDevConsole]);

  useEffect(() => {
    if (!address) {
      setXTaskStatus((prev) => ({ ...prev, connected: false, completed: false, error: "" }));
      setXConnectPendingVerify(false);
      setXFollowPendingVerify(false);
      setXLikeRtPendingVerify(false);
      setCapricornPendingVerify(false);
      savePendingVerifyState({ connect: false, follow: false, likeRt: false });
      setTaskCompletions({});
      loadPointsLeaderboard();
      return;
    }
    const pending = loadPendingVerifyState();
    setXConnectPendingVerify(Boolean(pending.connect));
    setXFollowPendingVerify(Boolean(pending.follow));
    setXLikeRtPendingVerify(Boolean(pending.likeRt));
    (async () => {
      const status = await refreshXFollowStatus(address, { silent: true });
      await loadTaskCompletions(address);
      await loadPointsLeaderboard();
      if (!status?.connected) {
        setXConnectPendingVerify(false);
        setXFollowPendingVerify(false);
        setXLikeRtPendingVerify(false);
        savePendingVerifyState({ connect: false, follow: false, likeRt: false });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useEffect(() => {
    const shouldPollCommunity = pageTab === "tasks" || pageTab === "community";
    if (!shouldPollCommunity) return;

    loadLatestTweet();
    loadPointsLeaderboard();
    const id = setInterval(() => {
      loadLatestTweet();
      loadPointsLeaderboard();
      if (address) {
        loadTaskCompletions(address);
      }
    }, TASKS_POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, pageTab]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if ((params.get("x_connected") === "1" || params.get("x_connected") === "0") && window.opener && !window.opener.closed) {
      const ok = params.get("x_connected") === "1";
      const err = params.get("x_error") || "";
      try {
        window.opener.postMessage({ type: "bob_x_connected", ok, error: err }, window.location.origin);
      } catch {}
      try {
        window.close();
      } catch {}
      return;
    }
    if (params.get("x_connected")) {
      if (address) {
        refreshXFollowStatus(address);
        loadTaskCompletions(address);
      }
      setXConnectPendingVerify(true);
      queueXConnectVerifyReset();
      params.delete("x_connected");
      params.delete("x_error");
      const qs = params.toString();
      const next = `${window.location.pathname}${qs ? `?${qs}` : ""}${window.location.hash || ""}`;
      window.history.replaceState({}, "", next);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useEffect(() => {
    const onMessage = (event) => {
      if (!event?.data || event.data.type !== "bob_x_connected") return;
      if (address) {
        refreshXFollowStatus(address);
        loadTaskCompletions(address);
      }
      setXConnectPendingVerify(true);
      queueXConnectVerifyReset();
      loadPointsLeaderboard();
      setXTaskStatus((prev) => ({ ...prev, loading: false, error: "" }));
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useEffect(() => {
    const capricornState = taskCompletions?.[CAPRICORN_LP_TASK_ID];
    const shouldBePending = Boolean(capricornState && !capricornState.completed && capricornState.started);
    setCapricornPendingVerify(shouldBePending);
  }, [taskCompletions]);

  useEffect(() => {
    const refreshOnReturn = () => {
      if (!address) return;
      loadTaskCompletions(address);
      loadPointsLeaderboard();
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") refreshOnReturn();
    };
    window.addEventListener("focus", refreshOnReturn);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("focus", refreshOnReturn);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address]);

  useEffect(() => {
    return () => {
      if (xConnectResetTimerRef.current) clearTimeout(xConnectResetTimerRef.current);
      if (xFollowResetTimerRef.current) clearTimeout(xFollowResetTimerRef.current);
      if (xLikeRtResetTimerRef.current) clearTimeout(xLikeRtResetTimerRef.current);
      if (capricornResetTimerRef.current) clearTimeout(capricornResetTimerRef.current);
    };
  }, []);

  const openCoinbaseOnramp = async () => {
    setOnrampErr("");
    if (!address) return setOnrampErr("Connect wallet first.");
    if (!walletSigned) return setOnrampErr("Complete Step 1 (Connect & Sign) first.");
    if (
      typeof window !== "undefined" &&
      window.location.protocol === "https:" &&
      /^http:\/\//i.test(ONRAMP_SESSION_ENDPOINT)
    ) {
      return setOnrampErr("Onramp endpoint must be HTTPS when dashboard is served over HTTPS.");
    }
    const ua = String(window?.navigator?.userAgent || "");
    const isMobile = /Android|iPhone|iPad|iPod/i.test(ua);
    const isInAppWalletBrowser = /Phantom|MetaMaskMobile|TrustWallet|CoinbaseWallet/i.test(ua);
    const useSameTabRedirect = isMobile || isInAppWalletBrowser;
    const popup = useSameTabRedirect
      ? null
      : window.open("", "coinbase_onramp", "popup=yes,width=460,height=780");
    if (!useSameTabRedirect && !popup) {
      return setOnrampErr("Popup blocked. Please allow popups for this site.");
    }
    setOnrampBusy(true);
    try {
      const res = await fetch(ONRAMP_SESSION_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ address, amount: Number(safeQuoteAmount) }),
      });
      const text = await res.text();
      let json = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch {
        json = null;
      }
      if (!res.ok || !json?.onrampUrl) {
        const fallback = `Failed to create Coinbase session (${res.status})`;
        throw new Error(json?.error || fallback);
      }
      if (useSameTabRedirect) {
        window.location.href = json.onrampUrl;
      } else {
        popup.location.href = json.onrampUrl;
      }
    } catch (err) {
      const msg = String(err?.message || "");
      if (msg.includes("Failed to fetch")) {
        setOnrampErr("Session endpoint unreachable. Check REACT_APP_ONRAMP_SESSION_ENDPOINT and nginx proxy.");
      } else {
        setOnrampErr(msg || "Coinbase unavailable right now.");
      }
      try {
        popup?.close();
      } catch {}
    } finally {
      setOnrampBusy(false);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.set("tab", pageTab);
    if (pageTab === "swap") url.searchParams.set("buy", buyTab);
    else url.searchParams.delete("buy");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [pageTab, buyTab]);

  useEffect(() => {
    const onPopState = () => {
      const next = getInitialTabs();
      setPageTab(next.page);
      setBuyTab(next.buy);
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    const applyCompactChart = () => {
      setCompactChart(window.innerWidth <= 1024);
      setUltraCompactWallets(window.innerWidth <= 512);
    };
    applyCompactChart();
    window.addEventListener("resize", applyCompactChart);
    return () => window.removeEventListener("resize", applyCompactChart);
  }, []);

  useEffect(() => {
    let rafId = 0;

    const applyNavOffset = () => {
      rafId = 0;
      const root = document.getElementById("bob-dashboard-page");
      const nav = document.querySelector("nav");
      if (!root || !(nav instanceof HTMLElement)) return;
      const navHeight = Math.ceil(nav.getBoundingClientRect().height);
      root.style.setProperty("--bd-nav-offset", `${navHeight}px`);
    };

    const onMeasure = () => {
      if (rafId) return;
      rafId = window.requestAnimationFrame(applyNavOffset);
    };

    applyNavOffset();
    window.addEventListener("resize", onMeasure);
    window.addEventListener("scroll", onMeasure, { passive: true });

    return () => {
      if (rafId) window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", onMeasure);
      window.removeEventListener("scroll", onMeasure);
    };
  }, []);


  const connectAndSignWallet = async () => {
    setOnrampErr("");
    setWalletSigned(false);
    if (!address) return setOnrampErr("Connect wallet first.");
    if (!walletClient) return setOnrampErr("Wallet client unavailable. Reconnect wallet and try again.");
    setSignBusy(true);
    try {
      const signer = String(address);
      const message = `BOB Onramp Verification\nWallet: ${signer}\nTimestamp: ${new Date().toISOString()}`;
      await walletClient.signMessage({ account: signer, message });
      setWalletSigned(true);
    } catch (err) {
      const msg = String(err?.message || "");
      if (msg.toLowerCase().includes("please request after current approval resolve")) {
        setOnrampErr("Wallet already has a pending approval. Confirm/reject it, then try signing again.");
      } else if (msg.toLowerCase().includes("not been authorized by the user")) {
        setOnrampErr("Wallet did not authorize this account for signing. Reconnect wallet and approve access.");
      } else {
        setOnrampErr(msg || "Wallet signature was cancelled.");
      }
    } finally {
      setSignBusy(false);
    }
  };

  useEffect(() => {
    if (!["analytics", "inventory"].includes(pageTab)) return;
    let dead = false;
    const loadPair = async () => {
      try {
        const res = await fetch(`${DASHBOARD_API_BASE}/api/dashboard/stats`);
        const json = await res.json().catch(() => null);
        if (dead || !json?.ok || !json?.data) throw new Error("Stats unavailable");
        setPair(json.data);
        writeDashboardCache("stats", json.data);
      } catch {
        if (dead) return;
        const cached = readDashboardCache("stats", null);
        if (cached) setPair(cached);
      }
    };
    loadPair();
    const id = setInterval(loadPair, ANALYTICS_STATS_POLL_MS);
    return () => { dead = true; clearInterval(id); };
  }, [pageTab]);

  useEffect(() => {
    if (!["analytics", "inventory"].includes(pageTab)) return;
    let dead = false;
    const loadHolders = async () => {
      try {
        const res = await fetch(`${DASHBOARD_API_BASE}/api/dashboard/holders?page=1&pageSize=100`);
        const json = await res.json().catch(() => null);
        if (dead || !json?.ok || !Array.isArray(json?.data)) {
          throw new Error(json?.error || "Holders endpoint unavailable");
        }
        const rows = json.data
          .map((r) => ({ wallet: String(r.wallet || "").toLowerCase(), balance: Number(r.balance || 0) }))
          .filter((r) => r.wallet && Number.isFinite(r.balance) && r.balance > 0)
          .sort((a, b) => b.balance - a.balance);
        setHolders(rows);
        setHolderCount(Number(json.total || rows.length || 0));
        setBurned(rows.filter((r) => BURN_WALLETS.has(r.wallet)).reduce((acc, r) => acc + r.balance, 0));
        setHoldersUpdatedAt(Number(json.updatedAt || Date.now()));
        setHoldersError(json.error ? String(json.error) : "");
        writeDashboardCache("holders_rows", rows);
        writeDashboardCache("holders_count", Number(json.total || rows.length || 0));
        writeDashboardCache("holders_burned", rows.filter((r) => BURN_WALLETS.has(r.wallet)).reduce((acc, r) => acc + r.balance, 0));
        writeDashboardCache("holders_updated_at", Number(json.updatedAt || Date.now()));
      } catch (err) {
        if (!dead) {
          console.warn("[analytics] holders fetch failed:", err?.message || err);
          setHolders(readDashboardCache("holders_rows", []));
          setHolderCount(readDashboardCache("holders_count", 0));
          setBurned(readDashboardCache("holders_burned", 0));
          setHoldersUpdatedAt(readDashboardCache("holders_updated_at", Date.now()));
          setHoldersError("");
        }
      }
    };
    loadHolders();
    const id = setInterval(loadHolders, ANALYTICS_HOLDERS_POLL_MS);
    return () => { dead = true; clearInterval(id); };
  }, [pageTab]);

  return (
    <div id="bob-dashboard-page">
      <div className="bd-bg" aria-hidden="true" style={{ "--bd-bg-image": 'url("/landing/bobspace.webp")' }} />
      <Nav useSmoothScroll={false} />

      <main className="bd-main">
        <section className="bd-page-tabs-sticky" aria-label="Dashboard section tabs">
          <div className="bd-page-tabs-card">
            <div className="bd-page-tabs" role="tablist" aria-label="Dashboard sections">
              <button type="button" className={`bd-page-tab-btn bd-page-tab-btn-analytics ${pageTab === "analytics" ? "is-active" : ""}`} onClick={() => setPageTab("analytics")}>
                <span className="bd-tab-icon" style={{ "--tab-icon": "url('/icons/analytics.svg')" }} aria-hidden="true" />
                <span className="bd-tab-label">Analytics</span>
              </button>
              <button type="button" className={`bd-page-tab-btn bd-page-tab-btn-swap ${pageTab === "swap" ? "is-active" : ""}`} onClick={() => setPageTab("swap")}>
                <span className="bd-tab-icon" style={{ "--tab-icon": "url('/icons/swap.svg')" }} aria-hidden="true" />
                <span className="bd-tab-label">Swap</span>
              </button>
              <button type="button" className={`bd-page-tab-btn bd-page-tab-btn-tasks ${pageTab === "tasks" ? "is-active" : ""}`} onClick={() => setPageTab("tasks")}>
                <span className="bd-tab-icon" style={{ "--tab-icon": "url('/icons/tasks.svg')" }} aria-hidden="true" />
                <span className="bd-tab-label">Tasks</span>
              </button>
              <button type="button" className={`bd-page-tab-btn bd-page-tab-btn-community ${pageTab === "community" ? "is-active" : ""}`} onClick={() => setPageTab("community")}>
                <span className="bd-tab-icon" style={{ "--tab-icon": "url('/icons/community.svg')" }} aria-hidden="true" />
                <span className="bd-tab-label">Community</span>
              </button>
              <button type="button" className={`bd-page-tab-btn bd-page-tab-btn-inventory ${pageTab === "inventory" ? "is-active" : ""}`} onClick={() => setPageTab("inventory")}>
                <span className="bd-tab-icon" style={{ "--tab-icon": "url('/icons/inventory.svg')" }} aria-hidden="true" />
                <span className="bd-tab-label">Inventory</span>
              </button>
            </div>
          </div>
        </section>

        <div className="bd-content-stack">

        {pageTab === "analytics" ? <AnalyticsLiveContent /> : null}

        {pageTab === "swap" ? (
            <section className="bd-card bd-buy-suite">
              <div className="bd-holders-head-main bd-header-stack">
                <p className="bd-kicker">Buy Paths</p>
                <h1>GET <span className="bd-accent">$BOB</span></h1>
                <p>Fiat, Monad swap, or bridge. Same destination.</p>
              </div>
            <div className="bd-buy-tabs-shell">
              <div className="bd-buy-tabs" role="tablist" aria-label="Buy paths">
                <button type="button" className={buyTab === "fiat" ? "is-active" : ""} onClick={() => setBuyTab("fiat")}>Fiat Onramp</button>
                <button type="button" className={buyTab === "exchange" ? "is-active" : ""} onClick={() => setBuyTab("exchange")}>Swap</button>
                <button type="button" className={buyTab === "bridge" ? "is-active" : ""} onClick={() => setBuyTab("bridge")}>Bridge</button>
              </div>
            </div>

            {buyTab === "fiat" ? (
              <div
                className="bd-buy-pane bd-buy-pane--fiat"
              >
                <div className="bd-pane-stack">
                  <aside className="bd-pane-info">
                    <p className="bd-pane-kicker">Fiat Path</p>
                    <h4>Buy <span className="bd-accent">$BOB</span> with Fiat</h4>
                    <p>Checkout with Coinbase, then route into BOB with one bridge step.</p>
                    <div className="bd-fiat-topline">
                      <span className="flow-node">FIAT</span>
                      <img className="flow-arrow-icon flow-arrow-icon-right" src="/icons/bracketarrowhollow-right.svg" alt="" aria-hidden="true" />
                      <span className="flow-node">ETH</span>
                      <img className="flow-arrow-icon flow-arrow-icon-right" src="/icons/bracketarrowhollow-right.svg" alt="" aria-hidden="true" />
                      <span className="flow-node">$BOB</span>
                    </div>
                  </aside>
                  <div className="bd-pane-main">
                    <div>
                      <div className="bd-fiat-meta-grid">
                        <div className="meta-cell"><span>Provider</span><strong>Coinbase Checkout</strong></div>
                        <div className="meta-cell">
                          <span>Fiat lands as</span>
                          <strong>ETH on chain</strong>
                        </div>
                        <div className="meta-cell"><span>Destination</span><strong>Wallet Signature</strong></div>
                      </div>
                      <div className="bd-fiat-callout">
                        <h4>Simple 3-step flow.</h4>
                        <p className="bd-fiat-sub">Sign wallet, buy ETH on Base, then bridge to BOB.</p>
                        <div className="bd-step-guide">
                          <div className={`bd-step-chip ${walletSigned ? "is-done" : "is-active"}`}>
                            <span className="bd-step-badge" aria-hidden="true">
                              <svg viewBox="0 0 24 24" width="20" height="20" role="presentation" focusable="false">
                                <circle cx="12" cy="12" r="11" className="bd-step-badge-circle" />
                                <text x="12" y="12" className="bd-step-badge-text">1</text>
                              </svg>
                            </span>
                            Connect &amp; Sign
                          </div>
                          <div className={`bd-step-chip ${walletSigned ? "is-active" : ""}`}>
                            <span className="bd-step-badge" aria-hidden="true">
                              <svg viewBox="0 0 24 24" width="20" height="20" role="presentation" focusable="false">
                                <circle cx="12" cy="12" r="11" className="bd-step-badge-circle" />
                                <text x="12" y="12" className="bd-step-badge-text">2</text>
                              </svg>
                            </span>
                            Buy ETH on Base
                          </div>
                          <div className="bd-step-chip">
                            <span className="bd-step-badge" aria-hidden="true">
                              <svg viewBox="0 0 24 24" width="20" height="20" role="presentation" focusable="false">
                                <circle cx="12" cy="12" r="11" className="bd-step-badge-circle" />
                                <text x="12" y="12" className="bd-step-badge-text">3</text>
                              </svg>
                            </span>
                            Bridge to Bob
                          </div>
                        </div>
                        <div className="bd-fiat-info-grid">
                          <div className="bd-fiat-info">
                            <strong>Input</strong>
                            <p>Card or bank payment.</p>
                          </div>
                          <div className="bd-fiat-info">
                            <strong>Midstep</strong>
                            <p>ETH received on Base.</p>
                          </div>
                          <div className="bd-fiat-info">
                            <strong>Outcome</strong>
                            <p>Get BOB on Monad.</p>
                          </div>
                        </div>
                        <div className="bd-buy-actions">
                          <button type="button" className={`bd-btn ${walletSigned ? "bd-btn-secondary" : "bd-btn-primary"}`} onClick={connectAndSignWallet} disabled={signBusy || onrampBusy}>
                            {signBusy ? "Waiting..." : (
                              <>
                                <span className="label-desktop">{walletSigned ? "Step 1 - Wallet Signed" : "Connect & Sign"}</span>
                                <span className="label-mobile">{walletSigned ? "Signed" : "Connect"}</span>
                              </>
                            )}
                          </button>
                          <button type="button" className={`bd-btn ${walletSigned ? "bd-btn-primary" : "bd-btn-secondary"}`} onClick={openCoinbaseOnramp} disabled={onrampBusy}>
                            {onrampBusy ? "Opening..." : (
                              <>
                                <span className="label-desktop">Step 2 - Buy ETH with Coinbase</span>
                                <span className="label-mobile">Buy ETH</span>
                              </>
                            )}
                          </button>
                          <button type="button" className="bd-btn bd-btn-secondary" onClick={() => setBuyTab("bridge")}>
                            <span className="label-desktop">Bridge to Bob</span>
                            <span className="label-mobile">Bridge</span>
                          </button>
                        </div>
                      </div>
                      {onrampErr ? <p className="bd-help">{onrampErr}</p> : null}
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {buyTab === "exchange" ? (
              <div className="bd-buy-pane bd-buy-pane--widget">
                <div className="bd-pane-stack">
                  <aside className="bd-pane-info">
                    <p className="bd-pane-kicker">Monad Swap</p>
                    <h4>Turn MON into <span className="bd-accent">$BOB</span> in one move.</h4>
                    <p>Enter an amount and execute a native Monad swap with live route selection under the hood.</p>
                  </aside>
                  <div className="bd-pane-main">
                    <div className="bd-jumper-embed-wrap"><LiFiWidget integrator="bobmonad-dashboard" config={lifiWidgetConfig} /></div>
                  </div>
                </div>
              </div>
            ) : null}

            {buyTab === "bridge" ? (
              <div className="bd-buy-pane bd-buy-pane--widget">
                <div className="bd-pane-stack">
                  <aside className="bd-pane-info">
                    <p className="bd-pane-kicker">Bridge Route</p>
                    <h4>Bring liquidity over and arrive as <span className="bd-accent">$BOB</span>.</h4>
                    <p>Start from your current chain and token, then route into BOB on Monad in one guided flow.</p>
                  </aside>
                  <div className="bd-pane-main">
                    <div className="bd-jumper-embed-wrap"><LiFiWidget integrator="bobmonad-dashboard" config={lifiBridgeWidgetConfig} /></div>
                  </div>
                </div>
              </div>
            ) : null}
          </section>
        ) : null}

        {pageTab === "tasks" ? <TasksContent /> : null}
        {pageTab === "community" ? <CommunityContent /> : null}

        {pageTab === "inventory" ? <InventoryContent /> : null}
        </div>
      </main>


      {isDevConnected ? (
        <button className="bd-dev-fab" type="button" onClick={() => setShowDevConsole((v) => !v)}>
          Dev Console
        </button>
      ) : null}
      {showDevConsole && isDevConnected ? (
        <div className="bd-dev-panel-wrap">
          <div className="bd-dev-panel">
            <div className="bd-dev-head">
              <h3>Dev Console</h3>
              <button type="button" className="bd-btn bd-btn-secondary" onClick={() => setShowDevConsole(false)}>
                Close
              </button>
            </div>
            <div className="bd-dev-row">
              <span>Task Preview</span>
              <div className="bd-dev-actions">
                <button
                  type="button"
                  className="bd-btn bd-btn-secondary"
                  onClick={() => setDevTaskPreviewMode("live")}
                  style={devTaskPreviewMode === "live" ? { borderColor: "rgba(245, 158, 11, 0.9)" } : {}}
                >
                  Live
                </button>
                <button
                  type="button"
                  className="bd-btn bd-btn-secondary"
                  onClick={() => setDevTaskPreviewMode("available")}
                  style={devTaskPreviewMode === "available" ? { borderColor: "rgba(245, 158, 11, 0.9)" } : {}}
                >
                  Preview Uncompleted
                </button>
                <button
                  type="button"
                  className="bd-btn bd-btn-secondary"
                  onClick={() => setDevTaskPreviewMode("completed")}
                  style={devTaskPreviewMode === "completed" ? { borderColor: "rgba(245, 158, 11, 0.9)" } : {}}
                >
                  Preview Completed
                </button>
              </div>
            </div>
            <div className="bd-dev-row">
              <span>Task Manager (Backend)</span>
              <div className="bd-dev-task-form">
                <label className="bd-dev-input-group">
                  <span>Task Title</span>
                  <input
                    type="text"
                    value={newTaskTitle}
                    onChange={(e) => setNewTaskTitle(e.target.value)}
                    placeholder="Enter task title"
                  />
                </label>
                <label className="bd-dev-input-group">
                  <span>Points</span>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={newTaskPoints}
                    onChange={(e) => setNewTaskPoints(e.target.value)}
                    placeholder="10"
                  />
                </label>
                <label className="bd-dev-input-group">
                  <span>Task URL (Optional)</span>
                  <input
                    type="url"
                    value={newTaskUrl}
                    onChange={(e) => setNewTaskUrl(e.target.value)}
                    placeholder="https://task-link"
                  />
                </label>
                <label className="bd-dev-input-group">
                  <span>Button Label (Optional)</span>
                  <input
                    type="text"
                    value={newTaskButtonLabel}
                    onChange={(e) => setNewTaskButtonLabel(e.target.value)}
                    placeholder="Open Task"
                  />
                </label>
                <button type="button" className="bd-btn bd-btn-secondary" onClick={addTaskFromDev}>
                  Add Task
                </button>
              </div>
              <span className="bd-dev-list-label">Current Removable Tasks</span>
              <label className="bd-dev-input-group">
                <span>Pinned Latest Tweet (Like + Retweet task)</span>
                <input
                  type="text"
                  value={devLatestTweetInput}
                  onChange={(e) => setDevLatestTweetInput(e.target.value)}
                  placeholder="Paste tweet URL or tweet ID"
                />
              </label>
              {devLatestTweetError ? <span className="bd-dev-error">{devLatestTweetError}</span> : null}
              <button type="button" className="bd-btn bd-btn-secondary" onClick={setLatestTweetFromDev}>
                Set Latest Tweet
              </button>
              {latestTweetId ? (
                <span className="bd-dev-list-label">
                  Current Tweet: {latestTweetId}
                </span>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      <Footer />
    </div>
  );
}
